import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost } from '../utils/api';

// Purchase row interface
export interface PurchaseRow {
  'S. No': number;
  'Medicine Name': string;
  'Composition Name': string;
  'Brand Name': string;
  'Batch Number': string;
  'storage_location': string;
  'HSN Code': string;
  'Quantity': number;
  'Quantity Type': string;
  'Per Quantity': number;
  'Cost Price': number;
  'Amount': number;
  'Discount': number;
  'Total Amount': number;
  'Free': number;
  'MRP': number;
  'Selling Price': number;
  'GST Rate': number;
  'Expiry Date': string; // YYYY-MM-DD format (stored internally), input shows MM-YYYY format
  'Line Total': number;
}

// Medicine suggestion interface
interface MedicineSuggestion {
  Name: string;
  Manufacturer: string;
  Salt_Composition: string;
  Quantity: string;
  Image_URL: string;
  key: string;
  relevance_score: number;
}

// Default test data for medicine search
const DEFAULT_MEDICINES: MedicineSuggestion[] = [
  {
    "Name": "Pantop DSR",
    "Manufacturer": "Sun Pharma",
    "Salt_Composition": "Pantoprazole, Domperidone",
    "Quantity": "10 strips",
    "Image_URL": "https://example.com/pantop.jpg",
    "key": "pharma-limited:test-004",
    "relevance_score": 100
  },
  {
    "Name": "Paracetamol 500mg",
    "Manufacturer": "Cipla",
    "Salt_Composition": "Paracetamol",
    "Quantity": "10 strips",
    "Image_URL": "https://example.com/paracetamol.jpg",
    "key": "pharma-limited:test-001",
    "relevance_score": 100
  },
  {
    "Name": "Dolo 650",
    "Manufacturer": "Micro Labs",
    "Salt_Composition": "Paracetamol",
    "Quantity": "15 strips",
    "Image_URL": "https://example.com/dolo.jpg",
    "key": "pharma-limited:test-002",
    "relevance_score": 50
  }
];

// Quantity Type options
const QUANTITY_TYPES = [
  'None',
  'Strip',
  'Bottles',
  'Syrup',
  'Tablet',
  'Box',
  'Tube',
  'Powder',
  'Vial',
  'Syringe',
  'Packet',
  'Pouch',
  'Capsule',
  'Individual',
  'Piece',
  'Sachets'
];


const rowHasData = (row: PurchaseRow): boolean => (
  (row['Medicine Name']?.trim?.() || '') !== '' ||
  (row['Brand Name']?.trim?.() || '') !== '' ||
  (row['Composition Name']?.trim?.() || '') !== '' ||
  (row['Batch Number']?.trim?.() || '') !== '' ||
  (row['storage_location']?.trim?.() || '') !== '' ||
  (row['Expiry Date']?.trim?.() || '') !== '' ||
  row['Quantity'] > 0 ||
  row['Per Quantity'] > 0 ||
  row['Amount'] > 0 ||
  row['MRP'] > 0 ||
  row['Selling Price'] > 0 ||
  row['GST Rate'] > 0
);

const createEmptyRow = (serialNumber: number = 1): PurchaseRow => ({
  'S. No': serialNumber,
  'Medicine Name': '',
  'Composition Name': '',
  'Brand Name': '',
  'Batch Number': '',
  'storage_location': '',
  'HSN Code': '',
  'Quantity': 0,
  'Quantity Type': 'None',
  'Per Quantity': 0,
  'Cost Price': 0,
  'Amount': 0,
  'Discount': 0,
  'Total Amount': 0,
  'Free': 0,
  'MRP': 0,
  'Selling Price': 0,
  'GST Rate': 0,
  'Expiry Date': '',
  'Line Total': 0,
});

// Helper function to calculate Line Total
const calculateLineTotal = (
  unitAmount: number,
  quantity: number,
  discountPercent: number,
  gstRate: number
): number => {
  const base = unitAmount * quantity;
  const discounted = base - (base * (discountPercent / 100));
  const total = discounted + (discounted * (gstRate / 100));
  return Math.round(total * 100) / 100; // Round to 2 decimals
};

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to check if expiry date is within 31 days or expired
const isExpiryDateWithin31Days = (expiryDate: string): boolean => {
  if (!expiryDate || expiryDate.trim() === '') {
    return false;
  }
  
  try {
    // Normalize the date to YYYY-MM-DD format first
    const normalizedDate = parseAndNormalizeDate(expiryDate);
    if (!normalizedDate) {
      return false;
    }
    
    const expiry = new Date(normalizedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    expiry.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Calculate difference in days
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Return true if expiry date is expired (past) or within 31 days (0-31 days from today)
    return diffDays <= 31;
  } catch (error) {
    return false;
  }
};

// Helper function to auto-resize textarea and update row height
const autoResizeTextarea = (textarea: HTMLTextAreaElement, rowIndex: number, updateRowHeight: (rowIndex: number, height: number) => void) => {
  textarea.style.height = 'auto';
  const scrollHeight = textarea.scrollHeight;
  const minHeight = 48; // Minimum height in pixels (matching input fields)
  const maxHeight = 200; // Maximum height in pixels
  const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
  textarea.style.height = `${newHeight}px`;
  textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  
  // Update row height to sync all fields in the row
  updateRowHeight(rowIndex, newHeight);
};

// Main EditablePurchaseTable component
export interface Distributor {
  id: number;
  name: string;
  contact?: string;
  phone?: string;
  email?: string;
  address?: string;
  gstNumber?: string;
  settledAmount?: number;
  unsettledAmount?: number;
  overpaidAmount?: number;
}

interface EditablePurchaseTableProps {
  onCsvUpload?: (csvData: any[], sourceType?: 'csv' | 'pdf') => void;
  onRegisterCsvMapping?: (applyFn: (csvData: any[], mapping: { [key: string]: string | string[] }) => void) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onUploadUsed?: () => void;
  onSubmitSuccess?: (message: string) => void;
  onSubmitError?: (message: string) => void;
  onAddDistributor?: () => void;
  onRegisterDistributorRefresh?: (refreshFn: () => void) => void;
  onEditDistributor?: (distributor: Distributor) => void;
  onRegisterFormReset?: (resetFn: () => void) => void;
  onImageClick?: (imageUrl: string, medicineName: string) => void;
  onRegisterGetData?: (getDataFn: () => { rows: PurchaseRow[]; selectedDistributor: Distributor | null }) => void;
  initialData?: { rows: PurchaseRow[]; selectedDistributor: Distributor | null };
}

const EditablePurchaseTable: React.FC<EditablePurchaseTableProps> = ({
  onCsvUpload,
  onRegisterCsvMapping,
  onUploadStart,
  onUploadEnd,
  onUploadUsed,
  onSubmitSuccess,
  onSubmitError,
  onAddDistributor,
  onRegisterDistributorRefresh,
  onEditDistributor,
  onRegisterFormReset,
  onImageClick,
  onRegisterGetData,
  initialData
}) => {
  // Selected distributor state
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isLoadingDistributors, setIsLoadingDistributors] = useState(false);
  const [showDistributorDropdown, setShowDistributorDropdown] = useState(false);
  const [distributorSearchTerm, setDistributorSearchTerm] = useState('');
  const distributorDropdownRef = useRef<HTMLDivElement | null>(null);
  const distributorSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const distributorInputRef = useRef<HTMLInputElement | null>(null);

  // Initialize with one default row
  const [rows, setRows] = useState<PurchaseRow[]>([createEmptyRow()]);

  // Initialize row heights for all rows on mount and when rows change
  useEffect(() => {
    const initialHeights: { [key: number]: number } = {};
    rows.forEach((_, index) => {
      if (!rowHeights[index]) {
        initialHeights[index] = 48;
      }
    });
    if (Object.keys(initialHeights).length > 0) {
      setRowHeights(prev => ({ ...prev, ...initialHeights }));
    }
  }, [rows.length]); // Only run when number of rows changes

  // Hydrate component state with saved purchase data (if provided)
  useEffect(() => {
    if (hasHydratedFromInitialData.current) {
      return;
    }

    if (!initialData) {
      return;
    }

    const hasRowData = Array.isArray(initialData.rows) && initialData.rows.length > 0;
    const hasDistributor = Boolean(initialData.selectedDistributor);

    if (!hasRowData && !hasDistributor) {
      return;
    }

    if (hasRowData) {
      const hydratedRows: PurchaseRow[] = initialData.rows.map((incomingRow: any, index: number) => {
        const safeRow = incomingRow || {};
        const sanitizedRow: PurchaseRow = {
          'S. No': index + 1,
          'Medicine Name': typeof safeRow['Medicine Name'] === 'string' ? sanitizeStringInput(safeRow['Medicine Name']) : '',
          'Composition Name': typeof safeRow['Composition Name'] === 'string' ? sanitizeStringInput(safeRow['Composition Name']) : '',
          'Brand Name': typeof safeRow['Brand Name'] === 'string' ? sanitizeStringInput(safeRow['Brand Name']) : '',
          'Batch Number': typeof safeRow['Batch Number'] === 'string' ? safeRow['Batch Number'] : '',
          'storage_location': typeof safeRow['storage_location'] === 'string' ? sanitizeStringInput(safeRow['storage_location']) : '',
          'HSN Code': typeof safeRow['HSN Code'] === 'string' ? sanitizeStringInput(safeRow['HSN Code']) : '',
          'Quantity': parseFloat(String(safeRow['Quantity'])) || 0,
          'Quantity Type': typeof safeRow['Quantity Type'] === 'string' && safeRow['Quantity Type'].trim() !== ''
            ? safeRow['Quantity Type']
            : 'None',
          'Per Quantity': extractNumericValue(safeRow['Per Quantity'] || safeRow['Tablets Per Strip'] || ''),
          'Cost Price': parseFloat(String(safeRow['Cost Price'] || safeRow['Amount'])) || 0,
          'Amount': parseFloat(String(safeRow['Amount'] || safeRow['Cost Price'])) || 0,
          'Discount': parseFloat(String(safeRow['Discount'])) || 0,
          'MRP': parseFloat(String(safeRow['MRP'])) || 0,
          'Selling Price': parseFloat(String(safeRow['Selling Price'])) || parseFloat(String(safeRow['MRP'])) || 0,
          'GST Rate': parseFloat(String(safeRow['GST Rate'])) || 0,
          'Total Amount': parseFloat(String(safeRow['Total Amount'])) || 0,
          'Free': parseFloat(String(safeRow['Free'])) || 0,
          'Expiry Date': typeof safeRow['Expiry Date'] === 'string' ? safeRow['Expiry Date'] : '',
          'Line Total': 0,
        };

        // Auto-calculate Total Amount = Cost Price × Quantity
        const costPrice = sanitizedRow['Cost Price'] || sanitizedRow['Amount'] || 0;
        const quantity = sanitizedRow['Quantity'] || 0;
        sanitizedRow['Total Amount'] = costPrice * quantity;

        sanitizedRow['Line Total'] = calculateLineTotal(
          sanitizedRow['Cost Price'] || sanitizedRow['Amount'] || 0,
          sanitizedRow['Quantity'] || 0,
          sanitizedRow['Discount'] || 0,
          sanitizedRow['GST Rate'] || 0
        );

        return sanitizedRow;
      });

      setRows(hydratedRows);
      setRowHeights({});
      setNumericDisplayValues({});
    }

    if (hasDistributor) {
      setSelectedDistributor(initialData.selectedDistributor || null);
    }

    hasHydratedFromInitialData.current = true;
  }, [initialData]);

  // Medicine search state
  const [medicineSuggestions, setMedicineSuggestions] = useState<{ [key: number]: MedicineSuggestion[] }>({});
  const [showDropdown, setShowDropdown] = useState<{ [key: number]: boolean }>({});
  const [activeDropdownRow, setActiveDropdownRow] = useState<number | null>(null);
  const [dropdownHeight, setDropdownHeight] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<{ [key: number]: boolean }>({});
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollableRef = useRef<HTMLDivElement | null>(null);
  const inputRefs = useRef<{ [key: number]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null }>({});
  const [rowHeights, setRowHeights] = useState<{ [key: number]: number }>({});
  const searchTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout | null }>({});
  const hasHydratedFromInitialData = useRef(false);
  const [showTipBlock, setShowTipBlock] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const clearSavedPurchaseData = () => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.removeItem('Purchase-data');
    } catch (error) {
      console.error('Failed to clear saved purchase data:', error);
    }
  };
  const resetFormState = () => {
    setRows([createEmptyRow()]);
    setSelectedDistributor(null);
    setRowHeights({});
    setNumericDisplayValues({});
    setInvoiceNumber('');
    setInvoiceDate('');
    setCurrentFieldMapping({});
    clearSavedPurchaseData();
  };
  const handleClearAll = () => {
    resetFormState();
  };
  // Track display values for numeric fields to preserve decimal input during typing
  const [numericDisplayValues, setNumericDisplayValues] = useState<{ [key: string]: string }>({});
  // Popup state for validation errors
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ rowIndex: number; rowNumber: number; missingFields: string[] }[]>([]);
  // State to prevent double submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State to track rows being removed (for animation)
  const [removingRows, setRemovingRows] = useState<Set<number>>(new Set());
  // State for file upload loading
  const [isUploading, setIsUploading] = useState(false);
  // Swap mode state - tracks which column is in swap mode (null if not in swap mode)
  const [swapModeColumn, setSwapModeColumn] = useState<keyof PurchaseRow | null>(null);
  // State to store current field mapping for display
  const [currentFieldMapping, setCurrentFieldMapping] = useState<{ [key: string]: string | string[] }>({});

  // Handle arrow key navigation (up and down)
  const handleArrowKeyNavigation = (e: React.KeyboardEvent, currentRowIndex: number, column: keyof PurchaseRow) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Skip navigation for non-editable columns
      if (column === 'S. No' || column === 'Line Total') {
        return;
      }

      // Prevent default behavior and stop propagation
      e.preventDefault();
      e.stopPropagation();

      let targetRowIndex: number;
      
      if (e.key === 'ArrowDown') {
        targetRowIndex = currentRowIndex + 1;
      } else {
        // ArrowUp
        targetRowIndex = currentRowIndex - 1;
      }
      
      // If target row exists, focus on the same column in that row
      if (targetRowIndex >= 0 && targetRowIndex < rows.length) {
        const targetFieldKey = `${targetRowIndex}-${column}`;
        const targetField = fieldRefs.current[targetFieldKey];
        
        if (targetField) {
          setTimeout(() => {
            targetField.focus();
            // For textareas and inputs, move cursor to end
            if (targetField instanceof HTMLTextAreaElement || targetField instanceof HTMLInputElement) {
              const length = targetField.value.length;
              targetField.setSelectionRange(length, length);
            }
          }, 0);
        }
      }
      // If ArrowDown on last row or ArrowUp on first row, do nothing (stay in place)
    }
  };

  // Column definitions - defined early so it's accessible in updateRowHeight (order: S. No, Medicine Name, Brand Name, Composition Name, HSN Code, Batch Number, Storage Location, Expiry Date, Quantity, Quantity Type, Per Quantity, Free, Cost Price, MRP, Discount, GST Rate, Total Amount, Line Total)
  const columns: (keyof PurchaseRow)[] = [
    'S. No',
    'Medicine Name',
    'Batch Number',
    'Expiry Date',
    'Quantity',
    'Quantity Type',
    'Per Quantity',
    'Free',
    'Cost Price',
    'MRP',
    'Selling Price',
    'Discount',
    'GST Rate',
    'Total Amount',
    'Line Total',
    'Brand Name',
    'Composition Name',
    'HSN Code',
    'storage_location',
  ];

  const getColumnLabel = (column: keyof PurchaseRow): string => {
    if (column === 'Cost Price') return 'Rate';
    if (column === 'Per Quantity') return 'per strip';
    return column;
  };
  
  // Function to update row height and sync all fields in the row
  const updateRowHeight = (rowIndex: number, height: number) => {
    setRowHeights(prev => {
      const currentHeight = prev[rowIndex] || 48;
      const newHeight = Math.max(currentHeight, height);
      if (newHeight !== currentHeight) {
        // Update all fields in the row to match the tallest field
        setTimeout(() => {
          columns.forEach((column) => {
            const fieldKey = `${rowIndex}-${column}`;
            const field = fieldRefs.current[fieldKey];
            if (field) {
              // Set height for all field types
              field.style.height = `${newHeight}px`;
              if (field instanceof HTMLTextAreaElement) {
                // For textareas, handle overflow
                field.style.overflowY = field.scrollHeight > newHeight ? 'auto' : 'hidden';
              } else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
                // For inputs and selects, ensure height is set
                field.style.height = `${newHeight}px`;
                field.style.minHeight = `${newHeight}px`;
              }
            }
          });
        }, 0);
        return { ...prev, [rowIndex]: newHeight };
      }
      return prev;
    });
  };

  // Effect to sync all field heights when rowHeights changes or rows change
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Sync all fields in all rows to match their row height
      rows.forEach((_, rowIndex) => {
        const height = rowHeights[rowIndex] || 48; // Default to 48px if no height set
        columns.forEach((column) => {
          // Skip S. No and Line Total as they're read-only displays
          if (column === 'S. No' || column === 'Line Total') return;
          
          const fieldKey = `${rowIndex}-${column}`;
          const field = fieldRefs.current[fieldKey];
          if (field) {
            // Set height for all field types - force same height regardless of content
            field.style.height = `${height}px`;
            if (field instanceof HTMLTextAreaElement) {
              // For textareas, handle overflow but maintain height
              field.style.overflowY = field.scrollHeight > height ? 'auto' : 'hidden';
              field.style.minHeight = `${height}px`;
              field.style.maxHeight = `${height}px`;
            } else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
              // For inputs and selects, ensure height, minHeight, and maxHeight are all set
              field.style.height = `${height}px`;
              field.style.minHeight = `${height}px`;
              field.style.maxHeight = `${height}px`;
            }
          }
        });
      });
    });
  }, [rowHeights, rows]);

  // Search medicines function - calls API
  const searchMedicines = async (query: string, rowIndex: number): Promise<MedicineSuggestion[]> => {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        console.error('Authentication token not found');
        return [];
      }

      // Construct API URL
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/search?name=${encodeURIComponent(query)}`;
      
      // Call the search API
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.error('Failed to search medicines:', response.status);
        return [];
      }

      const data = await response.json();
      
      // Handle different possible response formats
      let medicines: any[] = [];
      if (data.medicines && Array.isArray(data.medicines)) {
        medicines = data.medicines;
      } else if (Array.isArray(data)) {
        medicines = data;
      } else if (data.data && Array.isArray(data.data)) {
        medicines = data.data;
      } else {
        console.warn('Unknown response format:', Object.keys(data));
        return [];
      }

      // Map API response to MedicineSuggestion format
      const mappedMedicines = medicines.map((medicine: any) => ({
        Name: medicine.Name || '',
        Manufacturer: medicine.Manufacturer || '',
        Salt_Composition: medicine.Salt_Composition || '',
        Quantity: medicine.Quantity || '',
        Image_URL: medicine.Image_URL || '',
        key: medicine.key || `medicine-${medicine.Name || Date.now()}-${rowIndex}`,
        relevance_score: medicine.relevance_score || 0
      }));
    
      return mappedMedicines;
    } catch (error: any) {
      console.error('Error searching medicines:', error);
      return [];
    }
  };

  // Handle medicine name change with autocomplete
  const handleMedicineNameChange = (rowIndex: number, value: string) => {
    // Store EXACT text the user types (including spaces)
    handleCellChange(rowIndex, 'Medicine Name', value);

    // Clear previous timeout
    if (searchTimeoutRef.current[rowIndex]) {
      clearTimeout(searchTimeoutRef.current[rowIndex]);
    }

    // Use a sanitized copy ONLY for searching / emptiness checks
    const sanitizedValue = sanitizeStringInput(value);
    const valueToCheck = sanitizedValue;
    
    // If value is empty, clear suggestions and hide dropdown
    if (!valueToCheck || valueToCheck.trim().length === 0) {
      setMedicineSuggestions(prev => ({
        ...prev,
        [rowIndex]: []
      }));
      setShowDropdown(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      setIsSearching(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      setActiveDropdownRow(null);
      return;
    }

    // Set active dropdown row immediately so dropdown can show
    setActiveDropdownRow(rowIndex);

    // Set loading state and show dropdown
    setIsSearching(prev => ({
      ...prev,
      [rowIndex]: true
    }));
    setShowDropdown(prev => ({
      ...prev,
      [rowIndex]: true
    }));

    // Debounce API call (wait 300ms after user stops typing)
    // Use sanitized value for search (without quotes / bad chars)
    searchTimeoutRef.current[rowIndex] = setTimeout(async () => {
      const suggestions = await searchMedicines(valueToCheck, rowIndex);
      
      setMedicineSuggestions(prev => ({
        ...prev,
        [rowIndex]: suggestions
      }));
      
      // Keep dropdown open to show results or "No medicines found" message
      setShowDropdown(prev => ({
        ...prev,
        [rowIndex]: true
      }));
      
      setIsSearching(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      
      // Keep activeDropdownRow set
    setActiveDropdownRow(rowIndex);
    }, 300);
  };

  // Handle medicine selection from dropdown
  const handleMedicineSelect = (rowIndex: number, medicine: MedicineSuggestion) => {
    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    
    // Auto-fill fields from selected medicine (with sanitization)
    row['Medicine Name'] = sanitizeStringInput(medicine.Name || '');
    row['Brand Name'] = sanitizeStringInput(medicine.Manufacturer || '');
    row['Composition Name'] = sanitizeStringInput(medicine.Salt_Composition || '');
    
    // Recalculate Line Total after auto-fill
    row['Line Total'] = calculateLineTotal(
      row['Cost Price'] || row['Amount'] || 0,
      row['Quantity'] || 0,
      row['Discount'] || 0,
      row['GST Rate'] || 0
    );
    
    updatedRows[rowIndex] = row;
    setRows(updatedRows);
    
    // Hide dropdown
    setShowDropdown(prev => ({
      ...prev,
      [rowIndex]: false
    }));
    setActiveDropdownRow(null);
    setDropdownHeight(0);
    clearSavedPurchaseData();
  };

  // Update dropdown height when it's shown to expand container
  useEffect(() => {
    if (activeDropdownRow !== null && showDropdown[activeDropdownRow]) {
      // Fixed height for 3 items display (270px)
      setDropdownHeight(270);
    } else {
      setDropdownHeight(0);
    }
  }, [activeDropdownRow, showDropdown]);

  // Add a new field/row - integrated from Purchases-buttons.tsx
  const handleAddField = useCallback(() => {
    setRows(prevRows => {
      const newRow = createEmptyRow(prevRows.length + 1);
      setRowHeights(prevHeights => ({ ...prevHeights, [prevRows.length]: 48 }));
      return [...prevRows, newRow];
    });
  }, []);

  const handleRemoveRow = useCallback((rowIndex: number) => {
    // Mark row as being removed for visual feedback
    setRemovingRows(prev => new Set(prev).add(rowIndex));
    
    // Wait for animation to complete before actually removing
    setTimeout(() => {
      setRows(prevRows => {
        if (prevRows.length === 1) {
          setNumericDisplayValues({});
          clearSavedPurchaseData();
          setRemovingRows(new Set());
          return [createEmptyRow()];
        }
        const updatedRows = prevRows
          .filter((_, index) => index !== rowIndex)
          .map((row, idx) => ({
            ...row,
            'S. No': idx + 1
          }));
        setNumericDisplayValues(prev => {
          const newValues = { ...prev };
          Object.keys(newValues).forEach(key => {
            if (key.startsWith(`${rowIndex}-`)) {
              delete newValues[key];
            }
          });
          return newValues;
        });
        setRemovingRows(new Set());
        return updatedRows;
      });
    }, 300); // Animation duration
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdownRow !== null) {
        const target = event.target as HTMLElement;
        const inputRef = inputRefs.current[activeDropdownRow];
        const dropdownRef = dropdownRefs.current[activeDropdownRow];
        
        // Check if click is on dropdown item or dropdown container (allow it)
        const isDropdownClick = target.closest('[data-dropdown-item]');
        if (isDropdownClick) {
          return;
        }
        
        // Check if click is on the dropdown container itself
        if (dropdownRef && dropdownRef.contains(target)) {
          return;
        }
        
        // Check if click is on the input field (allow it)
        if (inputRef && inputRef.contains(target)) {
          return;
        }
        
        // Close dropdown if click is outside
        setShowDropdown(prev => ({
          ...prev,
          [activeDropdownRow]: false
        }));
        setActiveDropdownRow(null);
      }

      // Close distributor dropdown if clicking outside
      const distributorTarget = event.target as HTMLElement;
      if (showDistributorDropdown && distributorDropdownRef.current && !distributorDropdownRef.current.contains(distributorTarget)) {
        setShowDistributorDropdown(false);
        // Always clear search term when closing dropdown
        setDistributorSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdownRow, showDistributorDropdown]);

  // Keyboard shortcuts: 
  // Ctrl/Cmd + A: Add new row
  // Ctrl/Cmd + D: Delete last row
  // Ctrl/Cmd + Right/Left Arrow: Scroll table horizontally
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      if (event.ctrlKey || event.metaKey) {
        // Only work on desktop (not mobile)
        if (window.innerWidth < 768) return;

        const key = event.key.toLowerCase();

        // Ctrl+A: Add new row
        if (key === 'a') {
          event.preventDefault();
          event.stopPropagation();
          handleAddField();
          return;
        }

        // Ctrl+D: Delete last row
        if (key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          if (rows.length > 0) {
            handleRemoveRow(rows.length - 1);
          }
          return;
        }

        // Ctrl+Right/Left Arrow: Scroll table horizontally
        if (key === 'arrowright' || key === 'arrowleft') {
          // Try to scroll the inner scrollable div first, then the outer container
          const scrollAmount = 300;
          let scrolled = false;

          // Try inner scrollable div
          if (tableScrollableRef.current) {
            const currentScroll = tableScrollableRef.current.scrollLeft;
            const maxScroll = tableScrollableRef.current.scrollWidth - tableScrollableRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            }
          }

          // If inner div didn't scroll, try outer container
          if (!scrolled && tableContainerRef.current) {
            const currentScroll = tableContainerRef.current.scrollLeft;
            const maxScroll = tableContainerRef.current.scrollWidth - tableContainerRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleAddField, handleRemoveRow, rows.length]);

  // Helper function to validate numeric input (integers only)
  const handleNumericInput = (value: string): string => {
    // Remove all non-numeric characters, allowing only digits
    return value.replace(/[^0-9]/g, '');
  };

  // Helper function to validate numeric input with decimals (for GST Rate percentage)
  const handleDecimalInput = (value: string): string => {
    // Allow digits and a single decimal point
    // Remove all characters except digits and one decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

// Extract only the numeric portion from a value like "S10" or "10GM"
const extractNumericValue = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const match = String(value).match(/[\d.]+/g);
  if (!match) return 0;
  const numeric = parseFloat(match.join(''));
  return Number.isNaN(numeric) ? 0 : numeric;
};

// Helper function to format expiry date input as user types (auto-adds "-" separators for MM-YYYY, DD defaults to 01)
const formatExpiryDateInput = (value: string): string => {
  if (!value) return '';
  
  // Allow digits, "-", and "/" (for backward compatibility, convert "/" to "-")
  let cleaned = value.replace(/[^\d/-]/g, '').replace(/\//g, '-');
  
  // Don't allow "-" at the start
  if (cleaned.startsWith('-')) {
    cleaned = cleaned.substring(1);
  }
  
  // Split by "-" to handle month and year parts (user only enters MM-YYYY)
  const parts = cleaned.split('-');
  let monthPart = parts[0] || '';
  let yearPart = parts[1] || '';
  
  // Remove extra parts if more than 2 parts exist (ignore day if user accidentally enters it)
  if (parts.length > 2) {
    // If user entered DD-MM-YYYY, ignore DD and use MM-YYYY
    if (parts[0].length === 2 && parts[1].length === 2) {
      monthPart = parts[1] || '';
      yearPart = parts[2] || '';
    } else {
      monthPart = parts[0] || '';
      yearPart = parts.slice(1).join('') || '';
    }
  }
  
  // If user has typed digits without separators, auto-insert separator after month
  // Format: MM-YYYY (e.g., "122025" -> "12-2025")
  if (parts.length === 1 && cleaned.length > 0) {
    const digits = cleaned.replace(/-/g, '');
    if (digits.length <= 2) {
      // Just month part
      monthPart = digits;
    } else if (digits.length <= 6) {
      // Month + year
      monthPart = digits.substring(0, 2);
      yearPart = digits.substring(2);
    } else {
      // Too many digits, limit to MM-YYYY
      monthPart = digits.substring(0, 2);
      yearPart = digits.substring(2, 6);
    }
  }
  
  // Validate and limit month to valid range (01-12)
  if (monthPart.length > 0) {
    // Limit month to 2 digits maximum first
    if (monthPart.length > 2) {
      const extra = monthPart.substring(2);
      monthPart = monthPart.substring(0, 2);
      if (!yearPart) {
        yearPart = extra;
      }
    }
    
    // Validate month value - only validate when month is complete (2 digits)
    if (monthPart.length === 2) {
      const monthNum = parseInt(monthPart, 10);
      // If month > 12, limit to 12
      if (monthNum > 12) {
        monthPart = '12';
      } else if (monthNum === 0) {
        // If user enters "00", change to "01"
        monthPart = '01';
      }
    }
    // For single digit, allow user to type freely (will be validated when second digit is entered)
  }
  
  // Limit year to 4 digits maximum
  if (yearPart.length > 4) {
    yearPart = yearPart.substring(0, 4);
  }
  
  // Reconstruct the value with proper separators (MM-YYYY format)
  if (yearPart) {
    // Full date: MM-YYYY
    return `${monthPart}-${yearPart}`;
  } else if (monthPart) {
    // Partial: MM
    return monthPart;
  } else {
    return '';
  }
};

// Helper function to convert YYYY-MM-DD to MM-YYYY format for display (DD always defaults to 01)
const convertToDateFormat = (value: string): string => {
  if (!value || value.trim() === '') return '';
  
  // If already in YYYY-MM-DD format, convert to MM-YYYY (user only sees MM-YYYY)
  const yyyyMmDdMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDdMatch) {
    const [, year, month] = yyyyMmDdMatch;
    return `${month}-${year}`;
  }
  
  // If already in MM-YYYY format, return as-is
  if (/^\d{2}-\d{4}$/.test(value.trim())) {
    return value.trim();
  }
  
  // If in DD-MM-YYYY format, extract MM-YYYY
  const ddMmYyyyMatch = value.trim().match(/^\d{2}-(\d{2})-(\d{4})$/);
  if (ddMmYyyyMatch) {
    const [, month, year] = ddMmYyyyMatch;
    return `${month}-${year}`;
  }
  
  // If in YYYY-MM format, convert to MM-YYYY
  if (/^\d{4}-\d{2}$/.test(value.trim())) {
    const [year, month] = value.trim().split('-');
    return `${month}-${year}`;
  }
  
  // If in MM/YYYY format (backward compatibility), convert to MM-YYYY
  if (/^\d{2}\/\d{4}$/.test(value.trim())) {
    return value.trim().replace('/', '-');
  }
  
  return '';
};

// Normalize various date formats into YYYY-MM-DD format (DD always defaults to 01)
const parseAndNormalizeDate = (value: string): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';

  // Handle MM-YYYY format (primary format - user only enters MM-YYYY)
  const mmYyyyMatch = trimmed.match(/^(\d{1,2})-(\d{4})$/);
  if (mmYyyyMatch) {
    const month = Math.min(12, Math.max(1, Number(mmYyyyMatch[1])));
    const year = Number(mmYyyyMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle YYYY-MM-DD format (already normalized)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Handle DD-MM-YYYY format - extract MM-YYYY and set DD to 01
  const ddMmYyyyMatch = trimmed.match(/^\d{2}-(\d{1,2})-(\d{4})$/);
  if (ddMmYyyyMatch) {
    const month = Math.min(12, Math.max(1, Number(ddMmYyyyMatch[1])));
    const year = Number(ddMmYyyyMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle YYYY-MM format - set date to 01
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return `${trimmed}-01`;
  }

  // Handle MM/YYYY format (backward compatibility)
  const mmYyyySlashMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
  if (mmYyyySlashMatch) {
    const month = Math.min(12, Math.max(1, Number(mmYyyySlashMatch[1])));
    const year = Number(mmYyyySlashMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle MM/YY format (backward compatibility)
  const shortMatch = trimmed.match(/^(\d{1,2})[/-](\d{2})$/);
  if (shortMatch) {
    const month = Math.min(12, Math.max(1, Number(shortMatch[1])));
    let year = Number(shortMatch[2]);
    if (year < 100) {
      year += 2000; // assume 2000s for two-digit year
    }
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Already ISO format YYYY-MM-DD - ensure date is 01
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const match = trimmed.match(/^(\d{4}-\d{2})/);
    if (match) {
      return `${match[1]}-01`;
    }
    return trimmed;
  }

  // Fallback: try native Date parse
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }

  return '';
};

  // Helper function to sanitize string fields (Medicine Name, Composition Name, Brand Name)
  // Removes quotes and allows only letters, numbers, spaces, hyphens, commas, periods, and common punctuation
  const sanitizeStringInput = (value: string): string => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    
    // Split by spaces to preserve them, then process each part
    const parts = value.split(/(\s+)/); // Split but keep spaces in the array
    const processedParts = parts.map(part => {
      // If this part is only spaces, keep it as-is
      if (/^\s+$/.test(part)) {
        return part;
      }
      
      // Remove quotes from start and end
      let cleaned = part;
      while ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
             (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
        cleaned = cleaned.slice(1, -1);
      }
      
      // Remove any remaining quotes within the string
      cleaned = cleaned.replace(/["']/g, '');
      
      // Allow letters, numbers, hyphens, commas, periods, parentheses, ampersands, and slashes
      // Spaces are already preserved by the split/join approach
      cleaned = cleaned.replace(/[^a-zA-Z0-9\-.,()&/]/g, '');
      
      return cleaned;
    });
    
    // Join back together (spaces are preserved from the split)
    let result = processedParts.join('');
    
    // Only trim leading and trailing whitespace at the very end
    result = result.trim();
    
    return result;
  };

  // Update a specific field in a row
  const handleCellChange = (rowIndex: number, field: keyof PurchaseRow, value: string | number) => {
    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    const fieldKey = `${rowIndex}-${field}`;
    
    // Convert value to appropriate type
    if (field === 'Quantity' || field === 'Per Quantity' || field === 'Amount' || 
        field === 'Cost Price' || field === 'Discount' || field === 'MRP' || field === 'GST Rate' || 
        field === 'Total Amount' || field === 'Free' || field === 'Selling Price') {
      const stringValue = String(value);
      
      // Store the display value (preserves trailing decimal point)
      setNumericDisplayValues(prev => ({
        ...prev,
        [fieldKey]: stringValue
      }));
      
      // Convert to number for storage and calculations
      if (stringValue === '' || stringValue === null || stringValue === undefined || stringValue.trim() === '') {
        (row[field] as number) = 0;
      } else {
        const trimmedValue = stringValue.trim();
        // Handle edge cases like ".", "-", "-."
        if (trimmedValue === '.' || trimmedValue === '-' || trimmedValue === '-.') {
          (row[field] as number) = 0;
        } else {
          // For "Per Quantity" field, extract only numeric value from strings like "S10" or "10GM"
          if (field === 'Per Quantity') {
            (row[field] as number) = extractNumericValue(trimmedValue);
          } else {
            const numValue = Number(trimmedValue);
            (row[field] as number) = isNaN(numValue) ? 0 : numValue;
          }
        }
      }

      // Auto-fill Selling Price with MRP
      // Logic: 
      // 1. If Selling Price is 0 or empty, sync it with MRP.
      // 2. If Selling Price was previously equal to the OLD MRP (before this keystroke), 
      //    it means the user likely hasn't touched it, so keep syncing it with the NEW MRP.
      //    (This handles typing "1" -> "12" -> "120" correctly)
      if (field === 'MRP') {
        const newMrpValue = Number(stringValue) || 0;
        const currentSellingPrice = row['Selling Price'] || 0;
        
        // We need to know the *previous* MRP value to know if they were in sync.
        // But we don't store "previous row state" easily here.
        // A simple heuristic: 
        // If Selling Price is 0, definitely sync.
        // If Selling Price is NOT 0, we need to decide if we should overwrite it.
        // The issue "taking only one digit" happens if we only sync once.
        
        // Let's try a different approach:
        // We track if 'Selling Price' has been manually edited? No, that's complex.
        
        // Simpler fix for "taking only one digit":
        // When typing MRP "120", the sequence is "1", "12", "120".
        // Step 1: MRP="1". SP=0. SP becomes "1".
        // Step 2: MRP="12". SP="1". SP is NOT 0. So it doesn't update? That's the bug.
        
        // We need to allow updating SP if it "looks like" it's following MRP.
        // OR, we just say: If SP <= MRP (or some condition), update it? No.
        
        // If we want "auto fill if empty", strictly speaking:
        // User types "1". SP=0 -> SP becomes "1".
        // User types "2" (MRP="12"). SP="1". It is not empty. So it stays "1".
        // This explains why it "takes only one digit".
        
        // Improved Logic:
        // If the user is typing in MRP field, and Selling Price *was* equal to the *old* MRP (conceptually), update it.
        // Since we don't have old MRP, let's assume if SP is not 0, we check if it matches a prefix? No.
        
        // Let's just enforce: "If Selling Price is 0 OR (Selling Price < newMrp AND Selling Price seems to be a prefix of newMrp? No)"
        
        // Correct fix:
        // We should update Selling Price if it matches the value of MRP *before* this character was added?
        // Or simpler: If the user hasn't explicitly focused/edited Selling Price?
        
        // Let's use a flag or just say: 
        // If Selling Price is currently equal to the *numeric value of the string minus the last char*? 
        // That's getting messy.
        
        // Let's go with: Update Selling Price to match MRP if:
        // 1. Selling Price is 0
        // 2. OR Selling Price equals the currently displayed MRP (before this update)? No.
        
        // What if we just update Selling Price to MRP if Selling Price is NOT explicitly dirty?
        // We don't have a dirty flag.
        
        // Alternative: Update SP if SP == 0 OR (SP is "close" to old MRP).
        
        // Actually, the "taking one digit" issue is exactly:
        // 1. Type '1' in MRP. MRP=1. SP is 0 -> SP becomes 1.
        // 2. Type '2' in MRP. MRP=12. SP is 1. SP != 0. Logic stops. SP stays 1.
        
        // We need to continue updating SP as long as it "was" auto-filled.
        // How do we know it was auto-filled? We can check if SP == (MRP / 10) roughly? No.
        
        // Let's just always update SP if it is equal to the "previous state of MRP".
        // The previous state of MRP is roughly `Number(stringValue.slice(0, -1))` if appending.
        
        const oldMrpValueString = stringValue.length > 0 ? stringValue.slice(0, -1) : '';
        const oldMrpValue = Number(oldMrpValueString) || 0;
        
        // If SP equals the "old" MRP (before this keystroke), we assume it was auto-filled and should continue syncing.
        // Also handle the case where it was 0.
        if (currentSellingPrice === 0 || currentSellingPrice === oldMrpValue) {
             row['Selling Price'] = newMrpValue;
             setNumericDisplayValues(prev => ({
               ...prev,
               [`${rowIndex}-Selling Price`]: stringValue
             }));
        }
      }

      // Keep Amount and Cost Price in sync (Amount represents Cost Price)
      if (field === 'Amount') {
        row['Cost Price'] = row['Amount'];
      } else if (field === 'Cost Price') {
        row['Amount'] = row['Cost Price'];
      }

      // Auto-calculate Total Amount = Cost Price × Quantity
      // Update Total Amount when Cost Price, Amount, or Quantity changes
      if (field === 'Cost Price' || field === 'Amount' || field === 'Quantity') {
        const costPrice = row['Cost Price'] || row['Amount'] || 0;
        const quantity = row['Quantity'] || 0;
        row['Total Amount'] = costPrice * quantity;
      }
    } else if (field === 'Expiry Date') {
      // Special handling for Expiry Date - auto-format input as user types
      const rawValue = String(value || '');
      
      // Format the input (auto-add "-" separators for MM-YYYY)
      const formattedValue = formatExpiryDateInput(rawValue);
      
      // Always store the formatted value in display state for immediate feedback
      setNumericDisplayValues(prev => ({
        ...prev,
        [fieldKey]: formattedValue
      }));
      
      // Store the formatted value in the row (will be normalized on blur or when complete)
      (row[field] as string) = formattedValue;
      
      // If format is complete (MM-YYYY), also normalize it immediately
      if (/^\d{2}-\d{4}$/.test(formattedValue.trim())) {
        const normalized = parseAndNormalizeDate(formattedValue);
        (row[field] as string) = normalized;
        // Keep display as MM-YYYY format
        setNumericDisplayValues(prev => ({
          ...prev,
          [fieldKey]: formattedValue
        }));
      }
    } else if (field === 'Quantity Type') {
      const stringValue = String(value).trim();
      (row[field] as string) = stringValue;
      
      // When Quantity Type changes, we no longer automatically clear Per Quantity
      // or force it to 0. We respect the user's input for all quantity types.
      // However, we do clear the display value to allow fresh input if needed.
      // Update: If Quantity Type is not 'Strip', clear Per Quantity and disable it.
      if (stringValue !== 'Strip') {
        (row['Per Quantity'] as number) = 0;
        setNumericDisplayValues(prev => {
          const newValues = { ...prev };
          delete newValues[`${rowIndex}-Per Quantity`];
          return newValues;
        });
      }
      
      // Clear display value for this field
      setNumericDisplayValues(prev => {
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
    } else if (field !== 'S. No' && field !== 'Line Total') {
      let stringValue = String(value);
      
      // Apply string sanitization only for non‑name fields where we strictly control characters.
      // We deliberately DO NOT sanitize 'Medicine Name' and 'Composition Name' so that spaces
      // and other user-entered characters are preserved as typed.
      if (field === 'Brand Name' || field === 'HSN Code' || field === 'storage_location') {
        stringValue = sanitizeStringInput(stringValue);
      }
      
      (row[field] as string) = stringValue;
      
      // Clear display value for non-numeric fields
      setNumericDisplayValues(prev => {
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
    }
    
    // Always recalculate Line Total whenever any field changes
    // This ensures Line Total updates in real-time as user types
    row['Line Total'] = calculateLineTotal(
      row['Cost Price'] || row['Amount'] || 0,
      row['Quantity'] || 0,
      row['Discount'] || 0,
      row['GST Rate'] || 0
    );
    
    // Update S. No for all rows
    updatedRows[rowIndex] = row;
    updatedRows.forEach((r, idx) => {
      r['S. No'] = idx + 1;
    });
    
    setRows(updatedRows);
    clearSavedPurchaseData();
  };

  // Handle remove row

  // Build auto-mapping from first row keys to Purchase table fields (no popup)
  const buildAutoMapping = (firstItem: Record<string, unknown>): { [key: string]: string | string[] } => {
    const csvKeys = Object.keys(firstItem || {});
    const normalize = (s: string) => s.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    const aliases: { [target: string]: string[] } = {
      'Medicine Name': ['medicine name', 'medicine_name', 'name', 'product name', 'product_name', 'item name', 'medicine'],
      'Brand Name': ['brand name', 'brand_name', 'brand', 'manufacturer'],
      'Composition Name': ['composition name', 'composition_name', 'composition', 'salt composition', 'salt_composition'],
      'HSN Code': ['hsn code', 'hsn_code', 'hsn'],
      'Batch Number': ['batch number', 'batch_number', 'batch no', 'batch_no', 'batch'],
      'storage_location': ['storage location', 'storage_location', 'storage', 'location'],
      'Expiry Date': ['expiry date', 'expiry_date', 'exp date', 'exp_date', 'expiry', 'expiration'],
      'Quantity': ['quantity', 'qty'],
      'Quantity Type': ['quantity type', 'quantity_type', 'qty type', 'unit', 'quantity type'],
      'Per Quantity': ['tablets per strip', 'tablets_per_strip', 'per quantity', 'per_quantity'],
      'Free': ['free'],
      'Cost Price': ['cost price', 'cost_price', 'rate', 'unit price', 'unit_price', 'price'],
      'MRP': ['mrp'],
      'Selling Price': ['selling price', 'selling_price', 'sales price', 'sale price'],
      'Discount': ['discount', 'discount percent', 'discount_percent', 'discount %'],
      'GST Rate': ['gst rate', 'gst_rate', 'gst', 'tax', 'gst%', 'gst percent'],
      'Total Amount': ['total amount', 'total_amount', 'amount', 'line total', 'line_total'],
    };
    const mapping: { [key: string]: string | string[] } = {};
    for (const [target, possible] of Object.entries(aliases)) {
      const targetKey = target === 'Per Quantity' ? 'Tablets Per Strip' : target;
      const matched = csvKeys.find((k) => {
        const n = normalize(k);
        return possible.some((p) => n === p || n.includes(p) || p.includes(n));
      });
      if (matched) mapping[targetKey] = matched;
    }
    console.log('[Auto-mapping] Built mapping from first row keys:', mapping);
    return mapping;
  };

  // Apply CSV mapping to rows
  const applyCsvMapping = (csvData: any[], mapping: { [key: string]: string | string[] }) => {
    if (!csvData || csvData.length === 0) return;
    
    // Store the mapping for display (normalize keys to match column names)
    const normalizedMapping: { [key: string]: string | string[] } = {};
    Object.entries(mapping).forEach(([key, value]) => {
      // Map 'Tablets Per Strip' to 'Per Quantity' for consistency
      const normalizedKey = key === 'Tablets Per Strip' ? 'Per Quantity' : key;
      normalizedMapping[normalizedKey] = value;
    });
    setCurrentFieldMapping(normalizedMapping);
    console.log('[applyCsvMapping] Stored field mapping:', normalizedMapping);

    // Convert CSV data to PurchaseRow format based on mapping
    const newRows: PurchaseRow[] = csvData.map((csvItem, index) => {
      const row: PurchaseRow = {
        'S. No': index + 1,
        'Medicine Name': '',
        'Composition Name': '',
        'Brand Name': '',
        'Batch Number': '',
        'HSN Code': '',
        'Quantity': 0,
  'Quantity Type': 'None',
  'Per Quantity': 0,
        'Cost Price': 0,
        'Amount': 0,
        'Discount': 0,
        'Total Amount': 0,
        'Free': 0,
        'MRP': 0,
        'GST Rate': 0,
        'Expiry Date': '',
        'Line Total': 0,
      };

      // Apply mapping: map CSV fields to PurchaseRow fields
      Object.entries(mapping).forEach(([targetField, csvFieldOrFields]) => {
        // Map 'Tablets Per Strip' to 'Per Quantity' for internal use
        const actualFieldKey = targetField === 'Tablets Per Strip' ? 'Per Quantity' : targetField;
        const fieldKey = actualFieldKey as keyof PurchaseRow;
        const csvFields = Array.isArray(csvFieldOrFields) ? csvFieldOrFields : [csvFieldOrFields];
        
        // Special handling for GST Rate: sum multiple fields
        if (fieldKey === 'GST Rate' && csvFields.length > 1) {
          let sum = 0;
          csvFields.forEach(csvField => {
            if (csvItem[csvField] !== undefined && csvItem[csvField] !== null) {
              const value = csvItem[csvField];
              sum += parseFloat(String(value)) || 0;
            }
          });
          (row[fieldKey] as number) = sum;
        } else {
          // Single field mapping (or first field if array)
          const csvField = csvFields[0];
          if (csvItem[csvField] !== undefined && csvItem[csvField] !== null) {
            const value = csvItem[csvField];
            
            // Convert value based on field type
            if (fieldKey === 'Quantity' || fieldKey === 'Amount' || fieldKey === 'Cost Price' ||
              fieldKey === 'Discount' || fieldKey === 'Total Amount' || fieldKey === 'Free' || fieldKey === 'Selling Price') {
            (row[fieldKey] as number) = Number(value) || 0;
          } else if (fieldKey === 'Per Quantity') {
              // Extract only numeric value from strings like "S10" or "10GM"
              (row[fieldKey] as number) = extractNumericValue(value);
            } else if (fieldKey === 'MRP' || fieldKey === 'GST Rate') {
              (row[fieldKey] as number) = Number(value) || 0;
            } else if (fieldKey === 'Expiry Date') {
              // Parse and normalize date format (handles MM/YY -> YYYY-MM-DD)
              const dateValue = String(value || '').trim();
              (row[fieldKey] as string) = parseAndNormalizeDate(dateValue);
            } else if (fieldKey !== 'S. No' && fieldKey !== 'Line Total') {
              // String fields - apply sanitization for Medicine Name, Composition Name, Brand Name, HSN Code, and storage_location
            let stringValue = String(value) || '';
            if (fieldKey === 'Medicine Name' || fieldKey === 'Composition Name' || fieldKey === 'Brand Name' || 
                fieldKey === 'HSN Code' || fieldKey === 'storage_location') {
              stringValue = sanitizeStringInput(stringValue);
            }
              (row[fieldKey] as string) = stringValue;
            }
          }
        }
      });

      // Calculate Line Total for each row
      row['Line Total'] = calculateLineTotal(
        row['Cost Price'] || row['Amount'] || 0,
        row['Quantity'] || 0,
        row['Discount'] || 0,
        row['GST Rate'] || 0
      );

      // Keep Cost Price and Amount in sync if one is missing
      if (row['Cost Price'] > 0 && row['Amount'] === 0) {
        row['Amount'] = row['Cost Price'];
      } else if (row['Amount'] > 0 && row['Cost Price'] === 0) {
        row['Cost Price'] = row['Amount'];
      }

      // Auto-calculate Total Amount = Cost Price × Quantity
      const costPrice = row['Cost Price'] || row['Amount'] || 0;
      const quantity = row['Quantity'] || 0;
      row['Total Amount'] = costPrice * quantity;
      
      return row;
    });

    // Update rows with CSV data
    setRows(newRows);
    console.log('[applyCsvMapping] Applied rows count:', newRows.length, 'Sample row:', newRows[0]);
    // Clear display values when CSV data is applied
    setNumericDisplayValues({});
  };

  // Handle column data swap
  const handleSwapData = (fromColumn: keyof PurchaseRow, toColumn: keyof PurchaseRow) => {
    if (fromColumn === toColumn) {
      setSwapModeColumn(null);
      return;
    }

    setRows(prevRows => {
      return prevRows.map(row => {
        const newRow: PurchaseRow = { ...row };
        // Swap the data between columns (use a loosely typed view to satisfy TS)
        const rowAny = newRow as any;
        const temp = rowAny[fromColumn];
        rowAny[fromColumn] = rowAny[toColumn];
        rowAny[toColumn] = temp;
        
        // Recalculate Total Amount after swap (if Cost Price, Amount, or Quantity were involved)
        const costPrice = newRow['Cost Price'] || newRow['Amount'] || 0;
        const quantity = newRow['Quantity'] || 0;
        newRow['Total Amount'] = costPrice * quantity;

        // Recalculate Line Total after swap
        newRow['Line Total'] = calculateLineTotal(
          newRow['Cost Price'] || newRow['Amount'] || 0,
          newRow['Quantity'] || 0,
          newRow['Discount'] || 0,
          newRow['GST Rate'] || 0
        );
        
        return newRow;
      });
    });

    // Clear swap mode
    setSwapModeColumn(null);
  };

  // Handle swap button click
  const handleSwapButtonClick = (column: keyof PurchaseRow) => {
    if (swapModeColumn === column) {
      // If clicking the same swap button, cancel swap mode
      setSwapModeColumn(null);
    } else if (swapModeColumn) {
      // If already in swap mode, swap the data
      handleSwapData(swapModeColumn, column);
    } else {
      // Start swap mode
      setSwapModeColumn(column);
    }
  };

  // Handle column header click when in swap mode
  const handleColumnHeaderClick = (column: keyof PurchaseRow) => {
    if (swapModeColumn && swapModeColumn !== column) {
      handleSwapData(swapModeColumn, column);
    }
  };

  // Handle escape key to cancel swap mode
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && swapModeColumn) {
        setSwapModeColumn(null);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [swapModeColumn]);

  // Register applyCsvMapping function with parent
  useEffect(() => {
    if (onRegisterCsvMapping) {
      onRegisterCsvMapping(applyCsvMapping);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register form reset function with parent
  useEffect(() => {
    if (onRegisterFormReset) {
      onRegisterFormReset(() => {
        resetFormState();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterFormReset]);

  // Register getData function with parent
  useEffect(() => {
    if (onRegisterGetData) {
      onRegisterGetData(() => ({
        rows,
        selectedDistributor
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterGetData, rows, selectedDistributor]);

  // Fetch distributors function
  const fetchDistributors = async (searchTerm: string = '') => {
    try {
      setIsLoadingDistributors(true);
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        console.warn('No auth token found');
        return;
      }

      // Build URL with search parameter if provided
      let url = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors/';
      if (searchTerm && searchTerm.trim() !== '') {
        url += `?name=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array
        const distributorsArray = Array.isArray(data) ? data : (data.distributors || data.data || []);
        setDistributors(distributorsArray);
      } else {
        console.error('Failed to fetch distributors:', response.status);
        setDistributors([]);
      }
    } catch (error) {
      console.error('Error fetching distributors:', error);
      setDistributors([]);
    } finally {
      setIsLoadingDistributors(false);
    }
  };

  // Fetch distributors on component mount
  useEffect(() => {
    fetchDistributors();
  }, []);

  // Debounced search effect for distributors
  useEffect(() => {
    // Clear previous timeout
    if (distributorSearchTimeoutRef.current) {
      clearTimeout(distributorSearchTimeoutRef.current);
    }

    // Only search if dropdown is open AND there's a search term
    // Don't search on empty string - use the already loaded distributors
    if (showDistributorDropdown && distributorSearchTerm.trim() !== '') {
      // Set a new timeout for debounced search
      distributorSearchTimeoutRef.current = setTimeout(() => {
        fetchDistributors(distributorSearchTerm);
      }, 300); // 300ms debounce delay
    } else if (showDistributorDropdown && distributorSearchTerm.trim() === '' && distributors.length === 0) {
      // Only load initial distributors if dropdown is open, search is empty, and no distributors loaded yet
      fetchDistributors('');
    }
    // If dropdown opens with empty search term and distributors are already loaded, don't reload

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (distributorSearchTimeoutRef.current) {
        clearTimeout(distributorSearchTimeoutRef.current);
      }
    };
  }, [distributorSearchTerm, showDistributorDropdown]);

  // Register refresh function with parent
  useEffect(() => {
    if (onRegisterDistributorRefresh) {
      onRegisterDistributorRefresh(() => fetchDistributors(distributorSearchTerm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterDistributorRefresh, distributorSearchTerm]);

  // Auto-dismiss notification after 8 seconds
  useEffect(() => {
    if (showValidationPopup) {
      const timer = setTimeout(() => {
        setShowValidationPopup(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showValidationPopup]);

  // Calculate total value
  const calculateTotalValue = (): number => {
    return rows.reduce((sum, row) => sum + (row['Line Total'] || 0), 0);
  };
  const hasAnyRowData = rows.some(rowHasData);

  // Validate all rows and return missing fields
  const validateRows = (): { rowIndex: number; rowNumber: number; missingFields: string[] }[] => {
    const errors: { rowIndex: number; rowNumber: number; missingFields: string[] }[] = [];
    
    rows.forEach((row, index) => {
      const missingFields: string[] = [];
      
      // Check required fields
      if (!row['Medicine Name'] || row['Medicine Name'].trim() === '') {
        missingFields.push('Medicine Name');
      }
      if (!row['Batch Number'] || row['Batch Number'].trim() === '') {
        missingFields.push('Batch Number');
      }
      if (!row['Quantity'] || row['Quantity'] <= 0) {
        missingFields.push('Quantity');
      }
      // Check Quantity Type
      if (!row['Quantity Type'] || row['Quantity Type'].trim() === '' || row['Quantity Type'] === 'None') {
        missingFields.push('Quantity Type');
      }
      if (!row['Cost Price'] || row['Cost Price'] <= 0) {
        missingFields.push('Cost Price');
      }
      if (!row['MRP'] || row['MRP'] <= 0) {
        missingFields.push('MRP');
      }
      if (!row['Expiry Date'] || row['Expiry Date'].trim() === '') {
        missingFields.push('Expiry Date');
      }
      
      // Only add to errors if there are missing fields
      if (missingFields.length > 0) {
        errors.push({
          rowIndex: index,
          rowNumber: row['S. No'],
          missingFields
        });
      }
    });
    
    return errors;
  };

  // Handle submit
  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      // Validate all rows
      const validationErrors = validateRows();
      
      if (validationErrors.length > 0) {
        // Show popup with missing fields
        setValidationErrors(validationErrors);
        setShowValidationPopup(true);
        setIsSubmitting(false);
        return;
      }

      // Filter out empty rows and prepare data for API
      const validRows = rows.filter(row => 
        row['Medicine Name'].trim() !== '' && 
        row['Batch Number'].trim() !== '' &&
        row['Quantity'] > 0 &&
        row['Cost Price'] > 0 &&
        row['MRP'] > 0 &&
        row['Expiry Date'].trim() !== ''
      );

      if (validRows.length === 0) {
        alert('Please fill in at least one complete row with all required fields.');
        setIsSubmitting(false);
        return;
      }

      // Prepare medicines data according to API format
      const medicinesData = validRows.map((row) => {
        const quantityType = row['Quantity Type']?.trim() || '';
        const quantity = row['Quantity'] || 0;
        const perQuantity = parseFloat(String(row['Per Quantity'])) || 0;
        
        // Use the selected quantity type directly - do NOT default to 'Individual'
        const effectiveQuantityType = quantityType;
        
        const medicineData: any = {
          medicine_name: row['Medicine Name'].trim(),
          medicine_company: row['Brand Name']?.trim() || undefined,
          medicine_composition: row['Composition Name']?.trim() || undefined,
          batch_no: row['Batch Number'].trim(),
          expiry_date: row['Expiry Date'].trim(),
          quantity: quantity,
          free: parseFloat(String(row['Free'])) || 0,
          quantity_type: effectiveQuantityType || undefined,
          unit_cost: parseFloat(String(row['Cost Price'] || row['Amount'])) || 0.0,
          mrp: parseFloat(String(row['MRP'])) || 0.0,
          selling_price: parseFloat(String(row['Selling Price'])) || parseFloat(String(row['MRP'])) || 0.0,
          hsn_code: row['HSN Code']?.trim() || undefined,
          discount_percent: parseFloat(String(row['Discount'])) || 0.0,
          tax_percent: parseFloat(String(row['GST Rate'])) || 0.0,
          storage_location: row['storage_location']?.trim() || undefined,
          image_url: undefined,
        };
        
        // Send tablets_per_strip only when Quantity Type is Strip and Per Quantity has a value
        if (quantityType === 'Strip' && perQuantity > 0) {
          medicineData.tablets_per_strip = perQuantity;
        }
        
        return medicineData;
      });

      // Prepare request body according to API specification
      const requestBody: any = {
        distributor_id: selectedDistributor?.id || null,
        invoice_no: invoiceNumber.trim() || undefined,
        invoice_date: invoiceDate.trim() || undefined,
        // Use exact total value (float) instead of rounding to int
        settled_amount: calculateTotalValue(),
        payment_mode: 'credit',
        payment_details: {},
        notes: '',
        items: medicinesData,
      };

      // Print request body to console
      console.log('Purchase Submission Request Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        const successMessage = `Successfully submitted ${validRows.length} medicine(s)!`;
        
        // Reset the form to default state
        resetFormState();
        
        // Show notification instead of alert
        if (onSubmitSuccess) {
          onSubmitSuccess(successMessage);
        } else {
          alert(successMessage);
        }
        setIsSubmitting(false);
      } else {
        const errorText = await response.text();
        console.error('Failed to submit purchase:', response.status, errorText);
        
        // Try to parse error as JSON for better error handling
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch (e) {
          // If not JSON, use the text as is
        }
        
        const fullErrorMessage = `Failed to submit purchase (Status: ${response.status}): ${errorMessage}`;
        
        // Show notification instead of alert
        if (onSubmitError) {
          onSubmitError(fullErrorMessage);
        } else {
          alert(fullErrorMessage);
        }
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error('Network error:', error);
      
      // Enhanced error handling for CORS and network issues
      let errorMessage = '';
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED'))) {
        errorMessage = 'CORS Error: Unable to connect to the purchases API. Please check your connection and try again.';
      } else if (error.message && (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
                 error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
                 error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
                 error.message.includes('certificate'))) {
        errorMessage = 'SSL Certificate Error: The server certificate is invalid. Please contact the administrator.';
      } else {
        errorMessage = `Network error: ${error.message || 'Unknown error'}. Please check your connection and try again.`;
      }
      
      // Show notification instead of alert
      if (onSubmitError) {
        onSubmitError(errorMessage);
      } else {
        alert(errorMessage);
      }
      setIsSubmitting(false);
    }
  };

  // Column definitions are defined earlier in the component (line ~155)

  // Columns that should be right-aligned (numeric fields)
  const rightAlignColumns: (keyof PurchaseRow)[] = [
    'S. No',
    'Quantity',
    'Per Quantity',
    'Amount',
    'Discount',
    'Free',
    'MRP',
    'GST Rate',
    'Line Total',
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        margin: 0,
        padding: 0,
        alignItems: isMobile ? 'center' : 'stretch'
      }}>
        {/* Upload CSV, Select Distributor, and Add Field Buttons */}
      <div style={{
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : 'row',
        gridTemplateColumns: isMobile ? 'none' : '1fr 2fr 1fr',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'stretch',
        gap: '1rem',
        width: isMobile ? '100%' : '100%',
        padding: isMobile ? '0 0.5rem' : '0'
      }}>
        {/* Upload Doc Button - Left Side */}
        <div style={{ 
          marginLeft: isMobile ? '0' : '1.5rem', 
          display: 'flex', 
          justifyContent: 'center',
          width: isMobile ? '100%' : 'auto'
        }}>
          {/* Unified Document Upload Input */}
          <input
            type="file"
            accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf,image/*"
            id="doc-upload-input"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              
              // Determine file type
              const fileName = file.name.toLowerCase();
              const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
              const isExcelFile = ['.csv', '.xls', '.xlsx'].includes(fileExtension) || 
                                  file.type === 'text/csv' ||
                                  file.type === 'application/vnd.ms-excel' ||
                                  file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
              const isPdfOrImage = fileExtension === '.pdf' || 
                                  file.type === 'application/pdf' ||
                                  file.type.startsWith('image/');

              if (!isExcelFile && !isPdfOrImage) {
                alert('Please select a valid file (CSV, XLS, XLSX, PDF, or Image).');
                return;
              }

              try {
                // Set loading state and notify parent (Purchase-page shows overlay)
                setIsUploading(true);
                onUploadStart?.();

                // Get auth token from localStorage
                const authToken = localStorage.getItem('authToken');
                
                if (!authToken) {
                  alert('Authentication token not found. Please log in again.');
                  setIsUploading(false);
                  onUploadEnd?.();
                  return;
                }

                const formData = new FormData();
                formData.append('file', file);
                
                // Add distributor_id if selected
                if (selectedDistributor && selectedDistributor.id) {
                  formData.append('distributor_id', selectedDistributor.id.toString());
                }

                let response: Response;
                let result: any;
                let items: any[] = [];

                if (isExcelFile) {
                  // Call CSV API for Excel files (CSV, XLS, XLSX)
                  response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/csv-upload-medicines', {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                      'ngrok-skip-browser-warning': 'true',
                      'Authorization': `Bearer ${authToken}`
                    },
                    body: formData
                  });

                  if (!response.ok) {
                    const errorText = await response.text();
                    let errMessage = errorText;
                    try {
                      const errJson = JSON.parse(errorText);
                      errMessage = (errJson.detail ?? errJson.message ?? errJson.error ?? errorText);
                      if (typeof errMessage !== 'string') errMessage = errorText;
                    } catch {
                      // use errorText as-is
                    }
                    throw new Error(errMessage);
                  }

                  result = await response.json();
                  console.log('[Upload CSV/Excel] API response:', result);

                  // Get items from response (support multiple API response shapes)
                  if (Array.isArray(result)) {
                    items = result;
                  } else if (result && typeof result === 'object') {
                    const possible = [
                      result.medicines,
                      result.purchase?.items,
                      result.data,
                      result.items,
                      result.rows,
                      result.records,
                      result.results
                    ];
                    for (const arr of possible) {
                      if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
                        items = arr;
                        break;
                      }
                    }
                    // fallback: any array value in result
                    if (items.length === 0) {
                      const arr = Object.values(result).find((v) => Array.isArray(v) && v.length > 0);
                      if (Array.isArray(arr)) items = arr;
                    }
                  }
                  
                  if (items && items.length > 0) {
                    console.log('[Upload CSV/Excel] Items to auto-fill:', items.length, items);
                    const mapping = buildAutoMapping(items[0] || {});
                    console.log('[Upload CSV/Excel] Auto-mapping:', mapping);
                    applyCsvMapping(items, mapping);
                    onUploadUsed?.();
                    console.log('[Upload CSV/Excel] Table auto-filled with', items.length, 'rows');
                  } else {
                    const serverMessage = result?.detail || result?.message || result?.error;
                    const message = typeof serverMessage === 'string' && serverMessage.trim()
                      ? serverMessage
                      : 'No data found in file. Please check the file has valid data and try again.';
                    alert(message);
                  }
                } else if (isPdfOrImage) {
                  // Presigned upload: get upload URL
                  const contentType = file.type || 'image/jpeg';
                  const presignedResponse = await apiPost('ocr/presigned-upload', {
                    filename: file.name || 'invoice.jpg',
                    content_type: contentType
                  });

                  if (!presignedResponse.ok) {
                    const errorText = await presignedResponse.text();
                    let errMessage = errorText;
                    try {
                      const errJson = JSON.parse(errorText);
                      errMessage = (errJson.detail ?? errJson.message ?? errJson.error ?? errorText);
                      if (typeof errMessage !== 'string') errMessage = errorText;
                    } catch {
                      // use errorText as-is
                    }
                    throw new Error(errMessage);
                  }

                  const presignedData = await presignedResponse.json();
                  console.log('[Upload PDF/Image] Presigned response:', presignedData);
                  const { upload_url: uploadUrl, s3_key: s3Key } = presignedData;

                  if (!uploadUrl || !s3Key) {
                    throw new Error('Invalid response from presigned upload API');
                  }

                  // PUT file to S3
                  const putResponse = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': contentType },
                    body: file
                  });

                  if (!putResponse.ok) {
                    throw new Error(`Failed to upload file: ${putResponse.status} ${putResponse.statusText}`);
                  }
                  console.log('[Upload PDF/Image] S3 PUT success, s3_key:', s3Key);

                  // Call OCR process with s3_key
                  const processResponse = await apiPost('ocr/process', { s3_key: s3Key });

                  if (!processResponse.ok) {
                    const errorText = await processResponse.text();
                    let errMessage = errorText;
                    try {
                      const errJson = JSON.parse(errorText);
                      errMessage = (errJson.detail ?? errJson.message ?? errJson.error ?? errorText);
                      if (typeof errMessage !== 'string') errMessage = errorText;
                    } catch {
                      // use errorText as-is
                    }
                    throw new Error(errMessage);
                  }

                  result = await processResponse.json();
                  console.log('[Upload PDF/Image] OCR process response:', result);

                  // Extract data from OCR process response if present
                  let extractedData: any[] = [];
                  if (Array.isArray(result)) {
                    extractedData = result;
                  } else if (result && typeof result === 'object') {
                    const possible = [
                      result.data,
                      result.medicines,
                      result.items,
                      result.rows,
                      result.table,
                      result.extracted_data,
                      result.results
                    ];
                    for (const arr of possible) {
                      if (Array.isArray(arr) && arr.length > 0) {
                        extractedData = arr;
                        break;
                      }
                    }
                    if (extractedData.length === 0) {
                      const arr = Object.values(result).find((v) => Array.isArray(v) && v.length > 0);
                      if (Array.isArray(arr)) extractedData = arr;
                    }
                  }

                  if (extractedData && extractedData.length > 0) {
                    console.log('[Upload PDF/Image] Extracted data to auto-fill:', extractedData.length, extractedData);
                    const mapping = buildAutoMapping(extractedData[0] || {});
                    console.log('[Upload PDF/Image] Auto-mapping:', mapping);
                    applyCsvMapping(extractedData, mapping);
                    onUploadUsed?.();
                    console.log('[Upload PDF/Image] Table auto-filled with', extractedData.length, 'rows');
                  } else {
                    console.log('[Upload PDF/Image] No extracted data in response');
                    const serverMessage = result?.detail ?? result?.message ?? result?.error;
                    const message = typeof serverMessage === 'string' && serverMessage.trim()
                      ? serverMessage
                      : 'Document uploaded and sent for processing.';
                    alert(message);
                  }
                }
                
                // Clear the file input
                e.target.value = '';
                
              } catch (error: any) {
                console.error('File upload error:', error);
                alert(`Failed to upload file: ${error.message}`);
              } finally {
                // Always clear loading state and notify parent
                setIsUploading(false);
                onUploadEnd?.();
              }
            }}
          />
          {/* Upload Doc Button */}
          <button
            onClick={() => {
              document.getElementById('doc-upload-input')?.click();
            }}
            type="button"
            disabled={isUploading}
            style={{
              border: '2px solid #6366f1',
              color: '#6366f1',
              background: isUploading ? '#e0e7ff' : 'transparent',
              borderRadius: '8px',
              padding: isMobile ? '0.75rem 1rem' : '0.875rem 1.25rem',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              outline: 'none',
              opacity: isUploading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isUploading && e.currentTarget) {
                e.currentTarget.style.backgroundColor = '#eef2ff';
                e.currentTarget.style.borderColor = '#4f46e5';
                e.currentTarget.style.color = '#4f46e5';
              }
            }}
            onMouseLeave={(e) => {
              if (!isUploading && e.currentTarget) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#6366f1';
                e.currentTarget.style.color = '#6366f1';
              }
            }}
            onFocus={(e) => {
              if (!isUploading && e.currentTarget) {
                e.currentTarget.style.outline = '2px solid rgba(99, 102, 241, 0.5)';
                e.currentTarget.style.outlineOffset = '2px';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.outline = 'none';
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>{isUploading ? 'Uploading...' : 'Upload Doc'}</span>
          </button>
        </div>

        {/* Select Distributor and Add Distributor Button - Center */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          width: isMobile ? '100%' : '100%',
          order: isMobile ? 2 : 0,
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <div 
            ref={distributorDropdownRef}
            style={{
              position: 'relative',
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? '100%' : '350px',
              maxWidth: isMobile ? '100%' : '450px'
            }}
          >
            {/* Search Input Field */}
            <div style={{ position: 'relative' }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={distributorInputRef}
                type="text"
                placeholder={selectedDistributor && !showDistributorDropdown ? selectedDistributor.name : "Search distributors..."}
                value={showDistributorDropdown || !selectedDistributor ? distributorSearchTerm : selectedDistributor.name}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setDistributorSearchTerm(newValue);
                  if (!showDistributorDropdown) {
                    setShowDistributorDropdown(true);
                  }
                }}
                onFocus={() => {
                  // Open dropdown
                  if (!showDistributorDropdown) {
                    setShowDistributorDropdown(true);
                  }
                  
                  // Clear selected distributor if one is selected to allow new search
                  if (selectedDistributor) {
                    setSelectedDistributor(null);
                    setDistributorSearchTerm('');
                  }
                }}
                disabled={false}
                style={{
                  width: '100%',
                  padding: isMobile 
                    ? '0.75rem 2.5rem 0.75rem 2.5rem' 
                    : '0.875rem 2.5rem 0.875rem 2.5rem',
                  border: '2px solid rgba(226, 232, 240, 0.6)',
                  borderRadius: '12px',
                  fontSize: isMobile ? '0.875rem' : '0.9rem',
                  fontWeight: selectedDistributor ? '500' : '400',
                  color: isLoadingDistributors ? '#9ca3af' : selectedDistributor ? '#059669' : '#374151',
                  outline: 'none',
                  cursor: isLoadingDistributors ? 'not-allowed' : 'text',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxSizing: 'border-box',
                  background: isLoadingDistributors 
                    ? 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.9) 100%)'
                    : selectedDistributor
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: showDistributorDropdown 
                    ? '0 0 0 4px rgba(5, 150, 105, 0.1), 0 4px 16px rgba(5, 150, 105, 0.15)'
                    : '0 2px 8px rgba(0, 0, 0, 0.02)',
                  borderColor: showDistributorDropdown ? '#059669' : selectedDistributor ? 'rgba(16, 185, 129, 0.3)' : 'rgba(226, 232, 240, 0.6)'
                }}
                onMouseEnter={(e) => {
                  if (!isLoadingDistributors && e.currentTarget) {
                    e.currentTarget.style.borderColor = '#059669';
                    e.currentTarget.style.boxShadow = '0 0 0 4px rgba(5, 150, 105, 0.1), 0 4px 16px rgba(5, 150, 105, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showDistributorDropdown && e.currentTarget) {
                    e.currentTarget.style.borderColor = selectedDistributor ? 'rgba(16, 185, 129, 0.3)' : 'rgba(226, 232, 240, 0.6)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.02)';
                  }
                }}
              />
              {selectedDistributor && !showDistributorDropdown && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDistributor(null);
                    setDistributorSearchTerm('');
                  }}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    transition: 'color 0.2s ease',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#374151';
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9ca3af';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
              {isLoadingDistributors && (
                <div style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #d1d5db',
                  borderTop: '2px solid #6b7280',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              )}
            </div>

            {/* Modern Dropdown */}
            {showDistributorDropdown && !isLoadingDistributors && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                maxHeight: '400px',
                overflowY: 'auto',
                overflowX: 'hidden',
                animation: 'slideInUp 0.2s ease-out'
              }}>
                {distributors.length === 0 ? (
                  <div style={{
                    padding: '2rem 1rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    No distributors found
                  </div>
                ) : (
                  distributors.map((distributor) => (
                    <div
                      key={distributor.id}
                      onClick={() => {
                        setSelectedDistributor(distributor);
                        setShowDistributorDropdown(false);
                        setDistributorSearchTerm('');
                      }}
                      style={{
                        padding: '1rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'all 0.15s ease',
                        background: selectedDistributor?.id === distributor.id ? 'rgba(16, 185, 129, 0.05)' : 'white',
                        borderLeft: selectedDistributor?.id === distributor.id ? '3px solid #10b981' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.backgroundColor = selectedDistributor?.id === distributor.id 
                            ? 'rgba(16, 185, 129, 0.1)' 
                            : '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.backgroundColor = selectedDistributor?.id === distributor.id 
                            ? 'rgba(16, 185, 129, 0.05)' 
                            : 'white';
                        }
                      }}
                    >
                      {/* Distributor Name and Edit Button */}
                      <div style={{
                        fontWeight: '600',
                        color: '#1f2937',
                        fontSize: '0.9rem',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          flex: 1
                        }}>
                          {selectedDistributor?.id === distributor.id && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                          {distributor.name}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onEditDistributor) {
                              onEditDistributor(distributor);
                              setShowDistributorDropdown(false);
                            }
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.375rem',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            color: '#6b7280',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.background = '#f3f4f6';
                              e.currentTarget.style.color = '#10b981';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = '#6b7280';
                            }
                          }}
                          title="Edit Distributor"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </div>

                      {/* Distributor Details */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.375rem',
                        fontSize: '0.8rem',
                        color: '#6b7280'
                      }}>
                        {distributor.phone && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#6b7280' }}>
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <span>{distributor.phone}</span>
                          </div>
                        )}
                        {distributor.email && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#6b7280' }}>
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <span>{distributor.email}</span>
                          </div>
                        )}
                        {distributor.address && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#6b7280', marginTop: '0.125rem' }}>
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                              <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <span>{distributor.address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (onAddDistributor) {
                onAddDistributor();
              }
            }}
            type="button"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: isMobile ? '0.75rem 1rem' : '0.875rem 1.25rem',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              outline: 'none',
              whiteSpace: 'nowrap',
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }
            }}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.outline = '2px solid rgba(59, 130, 246, 0.5)';
                e.currentTarget.style.outlineOffset = '2px';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.outline = 'none';
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
            </svg>
            <span>Add Distributor</span>
          </button>
        </div>

        {/* Right Side - Item Count, Tip Button, and Add Field Button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          flexWrap: 'wrap',
          marginRight: isMobile ? '0' : '1.5rem',
          width: isMobile ? '100%' : 'auto',
          order: isMobile ? 3 : 0
        }}>
          {rows.length > 0 && (
            <div style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              fontWeight: '500'
            }}>
              {rows.length} {rows.length === 1 ? 'item' : 'items'} added
            </div>
          )}
          <button
            onClick={() => setShowTipBlock(!showTipBlock)}
            type="button"
            style={{
              background: showTipBlock ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
              border: showTipBlock ? 'none' : '1px solid #d1d5db',
              color: showTipBlock ? 'white' : '#6b7280',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!showTipBlock) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#1f2937';
              }
            }}
            onMouseLeave={(e) => {
              if (!showTipBlock) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }
            }}
            title="Keyboard Shortcuts"
          >
            <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
          </button>
          <button
            onClick={handleAddField}
            type="button"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: isMobile ? '0.75rem 1.25rem' : '0.875rem 1.5rem',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
            onFocus={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.outline = '2px solid rgba(16, 185, 129, 0.5)';
                e.currentTarget.style.outlineOffset = '2px';
              }
            }}
            onBlur={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.outline = 'none';
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add Field</span>
          </button>
        </div>
      </div>

      {/* Tip Block */}
      {showTipBlock && (
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '1px solid #93c5fd',
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '1.5rem',
          marginBottom: '1.5rem',
          marginLeft: isMobile ? '0.5rem' : '1.5rem',
          marginRight: isMobile ? '0.5rem' : '1.5rem',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.75rem'
          }}>
            <h3 style={{
              fontSize: isMobile ? '1rem' : '1.125rem',
              fontWeight: '700',
              color: '#1e40af',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              Keyboard Shortcuts
            </h3>
            <button
              onClick={() => setShowTipBlock(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '0.75rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  A
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Add New Row
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  D
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Delete Last Row
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
                flexWrap: 'wrap'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  ←
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>/</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  →
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Scroll Table Horizontally
              </div>
            </div>
          </div>
          <div style={{
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid #bfdbfe',
            fontSize: '0.75rem',
            color: '#6b7280',
            fontStyle: 'italic'
          }}>
            💡 Tip: These shortcuts work on desktop only
          </div>
        </div>
      )}

      {/* Mobile Card Layout */}
      {isMobile ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%',
          maxWidth: '100%',
          padding: '0 0.5rem',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {rows.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '3rem 1rem',
              textAlign: 'center',
              color: '#6b7280',
              border: '1px solid #e5e7eb'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ margin: '0 auto 1rem' }}>
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                No rows added yet
              </p>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                Click "Add Field" to start adding purchase items
              </p>
            </div>
          ) : (
            rows.map((row, rowIndex) => {
              const isRemoving = removingRows.has(rowIndex);
              return (
              <div
                key={rowIndex}
                className={isRemoving ? 'row-removing' : ''}
                style={{
                  background: isRemoving ? '#fee2e2' : 'white',
                  borderRadius: '12px',
                  border: isRemoving ? '2px solid #ef4444' : '1px solid #e5e7eb',
                  padding: '1rem',
                  boxShadow: isRemoving ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden'
                }}
              >
                {/* S. No Badge */}
                <div style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  fontWeight: '600',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem'
                }}>
                  #{row['S. No']}
                </div>

                {/* Field Name-Value Pairs */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  {columns.filter(col => col !== 'S. No').map((column) => {
                    const value = row[column];
                    const isReadOnly = column === 'Line Total' || (column === 'Per Quantity' && row['Quantity Type'] !== 'Strip');
                    const isRightAligned = rightAlignColumns.includes(column);

                    const inputStyle: React.CSSProperties = {
                      width: '100%',
                        padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      background: isReadOnly ? '#f3f4f6' : 'white',
                      color: '#111827',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                        margin: 0,
                        minHeight: '48px',
                        height: 'auto'
                    };

                    return (
                      <div
                        key={column}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem 0',
                          borderBottom: column !== 'Line Total' ? '1px solid #f3f4f6' : 'none'
                        }}
                      >
                        {/* Field Name - Left Side */}
                        <div style={{
                          flex: '0 0 40%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.125rem'
                        }}>
                          <div style={{
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            color: '#6b7280',
                            textTransform: 'capitalize',
                            textAlign: isRightAligned ? 'right' : 'left'
                          }}>
                            {getColumnLabel(column)}:
                          </div>
                          {/* Note for Cost Price */}
                          {column === 'Cost Price' && (
                            <div style={{
                              fontSize: '0.65rem',
                              color: '#9ca3af',
                              fontWeight: '400',
                              fontStyle: 'italic',
                              textTransform: 'none',
                              textAlign: isRightAligned ? 'right' : 'left',
                              width: '100%'
                            }}>
                              (per single item)
                            </div>
                          )}
                          {Object.keys(currentFieldMapping).length > 0 && (() => {
                            // Look up the mapped field name for this column
                            const mappedField = currentFieldMapping[column];
                            if (mappedField) {
                              const mappedName = Array.isArray(mappedField) ? mappedField.join(' + ') : mappedField;
                              return (
                                <div style={{
                                  fontSize: '0.65rem',
                                  color: '#9ca3af',
                                  fontWeight: '400',
                                  fontStyle: 'italic',
                                  textTransform: 'none',
                                  textAlign: 'center',
                                  width: '100%'
                                }}>
                                  ({mappedName})
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        {/* Field Value - Right Side */}
                        <div style={{
                          flex: '1 1 60%',
                          display: 'flex',
                          justifyContent: isRightAligned ? 'flex-end' : 'flex-start'
                        }}>
                          {column === 'Line Total' ? (
                            <span style={{
                              fontWeight: '700',
                              color: '#10b981',
                              fontSize: '0.9375rem'
                            }}>
                              {formatCurrency(Number(value))}
                            </span>
                          ) : column === 'Quantity Type' ? (
                            <select
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              onKeyDown={(e) => handleArrowKeyNavigation(e, rowIndex, column)}
                              style={{
                                ...inputStyle,
                                cursor: 'pointer',
                                background: String(value) !== 'Strip' && String(value) !== 'None' ? '#fff7ed' : 'white',
                                border: String(value) !== 'Strip' && String(value) !== 'None' ? '1px solid #f97316' : '1px solid #d1d5db',
                                width: '100%',
                                maxWidth: '200px',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                            >
                              {QUANTITY_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : column === 'Expiry Date' ? (
                            (() => {
                              const fieldKey = `${rowIndex}-${column}`;
                              const rawValue = String(value || '');
                              // Use display value if available (user is actively typing - shows MM-YYYY)
                              // Otherwise, format the raw value for display (shows MM-YYYY only, no DD)
                              let displayValue = numericDisplayValues[fieldKey];
                              if (displayValue === undefined) {
                                // Not actively typing - show MM-YYYY format (no DD)
                                const formatted = convertToDateFormat(rawValue);
                                // If formatted exists and is MM-YYYY, use it directly (no "01-" prefix)
                                if (formatted && /^\d{2}-\d{4}$/.test(formatted)) {
                                  displayValue = formatted;
                                } else if (!formatted && rawValue && (/^\d{1,2}-\d{1,4}$/.test(rawValue) || /^\d{1,2}\/\d{1,4}$/.test(rawValue))) {
                                  // User input format, use MM-YYYY directly
                                  const mmYyyy = rawValue.replace('/', '-');
                                  if (/^\d{2}-\d{4}$/.test(mmYyyy)) {
                                    displayValue = mmYyyy;
                                  } else {
                                    displayValue = rawValue;
                                  }
                                } else {
                                  displayValue = formatted || rawValue;
                                }
                              } else {
                                // User is actively typing - ensure it's MM-YYYY format (remove "01-" prefix if present)
                                if (displayValue && /^01-(\d{2}-\d{4})$/.test(displayValue.trim())) {
                                  displayValue = displayValue.trim().substring(3);
                                }
                              }
                              
                              const handleFocus = () => {
                                // When user focuses, ensure display value is MM-YYYY format
                                const currentDisplay = numericDisplayValues[fieldKey];
                                if (!currentDisplay) {
                                  // If no display value, extract MM-YYYY from rawValue
                                  const formatted = convertToDateFormat(rawValue);
                                  if (formatted && /^\d{2}-\d{4}$/.test(formatted)) {
                                    setNumericDisplayValues(prev => ({
                                      ...prev,
                                      [fieldKey]: formatted
                                    }));
                                  }
                                } else if (currentDisplay && /^01-(\d{2}-\d{4})$/.test(currentDisplay.trim())) {
                                  // Remove "01-" prefix if somehow present
                                  const mmYyyy = currentDisplay.trim().substring(3);
                                  setNumericDisplayValues(prev => ({
                                    ...prev,
                                    [fieldKey]: mmYyyy
                                  }));
                                }
                              };
                              
                              const handleBlur = () => {
                                let currentValue = numericDisplayValues[fieldKey] || rawValue;
                                // If display shows "01-MM-YYYY", extract "MM-YYYY" for processing
                                if (currentValue && /^01-(\d{2}-\d{4})$/.test(currentValue.trim())) {
                                  currentValue = currentValue.trim().substring(3); // Remove "01-" prefix
                                }
                                if (currentValue.trim() !== '') {
                                  const normalized = parseAndNormalizeDate(currentValue);
                                  const updatedRows = [...rows];
                                  updatedRows[rowIndex] = { ...updatedRows[rowIndex], 'Expiry Date': normalized };
                                  setRows(updatedRows);
                                  // Clear display value so it shows MM-YYYY format on next render
                                  setNumericDisplayValues(prev => {
                                    const newValues = { ...prev };
                                    delete newValues[fieldKey];
                                    return newValues;
                                  });
                                }
                              };
                              
                              return isExpiryDateWithin31Days(rawValue) ? (
                                <div style={{
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  padding: '2px',
                                  borderRadius: '8px',
                                  width: '100%',
                                  maxWidth: '200px'
                                }}>
                                  <input
                                    ref={(el) => { fieldRefs.current[fieldKey] = el; }}
                                    type="text"
                                    value={displayValue}
                                    onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                    onKeyDown={(e) => {
                                      handleArrowKeyNavigation(e, rowIndex, column);
                                    }}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    placeholder="MM-YYYY"
                                    pattern="\d{2}-\d{4}"
                                    style={{
                                      ...inputStyle,
                                      background: 'white',
                                      width: '100%',
                                      height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px',
                                      border: 'none',
                                      borderRadius: '6px'
                                    }}
                                  />
                                </div>
                              ) : (
                                <input
                                  ref={(el) => { fieldRefs.current[fieldKey] = el; }}
                                  type="text"
                                  value={displayValue}
                                  onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                  onKeyDown={(e) => {
                                    handleArrowKeyNavigation(e, rowIndex, column);
                                  }}
                                  onBlur={handleBlur}
                                  placeholder="MM-YYYY"
                                  pattern="\d{2}-\d{4}"
                                  style={{
                                    ...inputStyle,
                                    background: 'white',
                                    width: '100%',
                                    maxWidth: '200px',
                                    height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                  }}
                                />
                              );
                            })()
                          ) : column === 'Medicine Name' ? (
                            <div style={{ 
                              position: 'relative', 
                              width: '100%',
                              maxWidth: '200px',
                              zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 1000 : 1
                            }} ref={(el) => { dropdownRefs.current[rowIndex] = el; }}>
                              <textarea
                                ref={(el) => { 
                                  inputRefs.current[rowIndex] = el;
                                  fieldRefs.current[`${rowIndex}-Medicine Name`] = el;
                                }}
                                value={value}
                                onChange={(e) => {
                                  handleMedicineNameChange(rowIndex, e.target.value);
                                  autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                                }}
                                placeholder="Enter Medicine Name"
                                rows={1}
                                style={{
                                  ...inputStyle,
                                  cursor: 'text',
                                  position: 'relative',
                                  zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 10 : 1,
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  minHeight: '48px',
                                  maxHeight: '200px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.5',
                                  height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                }}
                                onKeyDown={(e) => handleArrowKeyNavigation(e, rowIndex, 'Medicine Name')}
                                onFocus={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.borderColor = '#10b981';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                                  }
                                  // Show dropdown if there are suggestions or if we're searching
                                  if ((medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0) || isSearching[rowIndex]) {
                                    setShowDropdown(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                    setActiveDropdownRow(rowIndex);
                                  }
                                }}
                                onBlur={(e) => {
                                  const target = e.currentTarget;
                                  setTimeout(() => {
                                    if (target) {
                                      target.style.borderColor = '#d1d5db';
                                      target.style.boxShadow = 'none';
                                    }
                                  }, 200);
                                }}
                                onInput={(e) => {
                                  autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                                }}
                              />
                              {/* Mobile Dropdown - Positioned below Medicine Name input */}
                              {activeDropdownRow === rowIndex && (showDropdown[rowIndex] || isSearching[rowIndex]) && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    right: 0,
                                    width: '100%',
                                    background: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    zIndex: 1001,
                                    maxHeight: '270px',
                                    overflowY: 'auto'
                                  }}
                                >
                                  {isSearching[rowIndex] ? (
                                    <div style={{
                                      padding: '1.5rem',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '0.875rem'
                                    }}>
                                      Searching...
                                    </div>
                                  ) : medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0 ? (
                                    medicineSuggestions[rowIndex].map((medicine, medIndex) => (
                                    <div
                                      key={medicine.key}
                                      data-dropdown-item
                                      onClick={() => handleMedicineSelect(rowIndex, medicine)}
                                      style={{
                                        padding: '0.75rem 1rem',
                                        cursor: 'pointer',
                                        borderBottom: medIndex < medicineSuggestions[rowIndex].length - 1 ? '1px solid #f3f4f6' : 'none',
                                        transition: 'background-color 0.15s ease',
                                        background: 'white'
                                      }}
                                      onTouchStart={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = '#f0fdf4';
                                        }
                                      }}
                                      onTouchEnd={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = 'white';
                                        }
                                      }}
                                    >
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: '0.75rem',
                                          width: '100%'
                                        }}>
                                          {/* Medicine Image */}
                                          {medicine.Image_URL && (
                                            <div 
                                              style={{
                                                flexShrink: 0,
                                                width: '50px',
                                                height: '50px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f3f4f6',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                cursor: 'pointer'
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (onImageClick) {
                                                  onImageClick(medicine.Image_URL, medicine.Name);
                                                }
                                              }}
                                            >
                                              <img 
                                                src={medicine.Image_URL} 
                                                alt={medicine.Name}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                  (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                              />
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (onImageClick) {
                                                    onImageClick(medicine.Image_URL, medicine.Name);
                                                  }
                                                }}
                                                style={{
                                                  position: 'absolute',
                                                  top: '2px',
                                                  right: '2px',
                                                  width: '20px',
                                                  height: '20px',
                                                  background: 'rgba(0, 0, 0, 0.6)',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s ease',
                                                  zIndex: 10
                                                }}
                                                onTouchStart={(e) => {
                                                  if (e.currentTarget) {
                                                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                                                    e.currentTarget.style.transform = 'scale(1.1)';
                                                  }
                                                }}
                                                onTouchEnd={(e) => {
                                                  if (e.currentTarget) {
                                                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                  }
                                                }}
                                              >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                                                </svg>
                                              </button>
                                            </div>
                                          )}
                                          
                                          {/* Medicine Details */}
                                          <div style={{
                                            flex: 1,
                                            minWidth: 0
                                          }}>
                                      <div style={{
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        fontSize: '0.875rem',
                                        marginBottom: '0.25rem'
                                      }}>
                                        {medicine.Name}
                                      </div>
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        marginBottom: '0.125rem'
                                      }}>
                                        <strong>Manufacturer:</strong> {medicine.Manufacturer}
                                      </div>
                                      <div style={{
                                        fontSize: '0.75rem',
                                              color: '#6b7280',
                                              marginBottom: medicine.Quantity ? '0.125rem' : '0'
                                      }}>
                                        <strong>Composition:</strong> {medicine.Salt_Composition}
                                      </div>
                                            {medicine.Quantity && (
                                              <div style={{
                                                fontSize: '0.75rem',
                                                color: '#6b7280'
                                              }}>
                                                <strong>Quantity:</strong> {medicine.Quantity}
                                    </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{
                                      padding: '1.5rem',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '0.875rem'
                                    }}>
                                      No medicines found
                                </div>
                              )}
                            </div>
                              )}
                            </div>
                          ) : (column === 'Composition Name' || column === 'Brand Name' || 
                                column === 'Batch Number' || column === 'HSN Code' || column === 'storage_location') ? (
                            <textarea
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, column, e.target.value);
                                autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              rows={1}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                width: '100%',
                                maxWidth: '200px',
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '48px',
                                maxHeight: '200px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                textAlign: isRightAligned ? 'right' : 'left',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onKeyDown={(e) => !isReadOnly && handleArrowKeyNavigation(e, rowIndex, column)}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#10b981';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                }
                                autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              onInput={(e) => {
                                autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                              }}
                            />
                          ) : (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              inputMode={(
                                column === 'Quantity' ||
                                column === 'Per Quantity' ||
                                column === 'Amount' ||
                                column === 'Discount' ||
                                column === 'Cost Price' ||
                                column === 'MRP' || 
                                    column === 'Selling Price' ||
                                    column === 'GST Rate' ||
                                column === 'Total Amount' ||
                                column === 'Free'
                              ) ? 'decimal' : undefined}
                              value={(
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'MRP' || 
                                    column === 'Selling Price' ||
                                    column === 'GST Rate' ||
                                    column === 'Total Amount' ||
                                    column === 'Free')
                                ? (numericDisplayValues[`${rowIndex}-${column}`] !== undefined 
                                    ? numericDisplayValues[`${rowIndex}-${column}`] 
                                    : (value === 0 ? '' : String(value)))
                                : String(value)}
                              onChange={(e) => {
                                  const isNumericField =
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'MRP' || 
                                    column === 'Selling Price' ||
                                    column === 'GST Rate' ||
                                    column === 'Total Amount' ||
                                    column === 'Free';
                                if (isNumericField) {
                                  // Allow decimals for all numeric fields
                                  const filteredValue = handleDecimalInput(e.target.value);
                                  handleCellChange(rowIndex, column, filteredValue);
                                } else {
                                  handleCellChange(rowIndex, column, e.target.value);
                                }
                              }}
                              placeholder={column === 'Cost Price' ? 'Enter cost per single item' : `Enter ${column}${column === 'GST Rate' ? ' (%)' : ''}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                width: '100%',
                                maxWidth: '200px',
                                textAlign: isRightAligned ? 'right' : 'left',
                                background: isReadOnly ? '#f3f4f6' : 'white',
                                opacity: 1,
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onKeyDown={(e) => !isReadOnly && handleArrowKeyNavigation(e, rowIndex, column)}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#10b981';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                  // Clear display value on blur to show clean number
                                  const isNumericField =
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'Total Amount' ||
                                    column === 'Free' ||
                                    column === 'MRP' ||
                                    column === 'Selling Price' ||
                                    column === 'GST Rate';
                                  if (isNumericField) {
                                    setNumericDisplayValues(prev => {
                                      const newValues = { ...prev };
                                      delete newValues[`${rowIndex}-${column}`];
                                      return newValues;
                                    });
                                  }
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Remove and Add Item Buttons */}
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handleRemoveRow(rowIndex)}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.625rem 1.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                      flex: '1 1 45%',
                      minWidth: '120px'
                    }}
                    onTouchStart={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      <line x1="10" y1="11" x2="10" y2="17"/>
                      <line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                    Remove Item
                  </button>
                  <button
                    onClick={handleAddField}
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.625rem 1.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                      flex: '1 1 45%',
                      minWidth: '120px'
                    }}
                    onTouchStart={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Item
                  </button>
                </div>

              </div>
            );
            })
          )}
        </div>
      ) : (
        /* Desktop Table Layout */
        <div 
          ref={tableContainerRef}
          className="purchase-table-scroll-container"
          style={{
            border: '2px solid #e5e7eb',
            borderRadius: '0',
            overflowX: 'auto',
            overflowY: 'visible',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            background: 'white',
            margin: 0,
            padding: 0,
            paddingBottom: activeDropdownRow !== null && showDropdown[activeDropdownRow] && dropdownHeight > 0
              ? `${dropdownHeight + 10}px`
              : '0',
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            maxWidth: '100%',
            minHeight: 'fit-content',
            position: 'relative',
            transition: 'padding-bottom 0.2s ease',
            display: 'block'
          }}
        >
          <div 
            ref={tableScrollableRef}
            style={{
              display: 'block',
              width: 'max-content',
              minWidth: '100%'
            }}
          >
            <table style={{
              borderCollapse: 'collapse',
              fontSize: '0.875rem',
              margin: 0,
              padding: 0,
              borderSpacing: 0,
              width: 'max-content',
              minWidth: '100%',
              tableLayout: 'auto'
            }}>
            <thead>
              <tr style={{
                background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                borderBottom: '2px solid #d1d5db'
              }}>
                {columns.map((column, index) => {
                  const isSwapMode = swapModeColumn !== null;
                  const isSwapSource = swapModeColumn === column;
                  const isSwapTarget = isSwapMode && swapModeColumn !== column && column !== 'S. No' && column !== 'Line Total';
                  
                  return (
                    <th
                      key={column}
                      onClick={() => isSwapTarget && handleColumnHeaderClick(column)}
                      style={{
                        padding: index === 0 ? '1rem 0.75rem 1rem 0' : '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: isSwapSource ? '#dc2626' : (isSwapTarget ? '#2563eb' : '#374151'),
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: isSwapSource 
                          ? 'linear-gradient(to bottom, #fee2e2, #fecaca)' 
                          : (isSwapTarget 
                            ? 'linear-gradient(to bottom, #dbeafe, #bfdbfe)' 
                            : 'linear-gradient(to bottom, #f9fafb, #f3f4f6)'),
                        whiteSpace: 'nowrap',
                        minWidth: '150px',
                        margin: 0,
                        cursor: isSwapTarget ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                        border: isSwapSource ? '2px solid #dc2626' : (isSwapTarget ? '2px solid #2563eb' : 'none'),
                        borderRadius: isSwapSource || isSwapTarget ? '4px' : '0'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.25rem',
                        width: '100%'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          width: '100%'
                        }}>
                          <span>{getColumnLabel(column)}</span>
                          {column !== 'S. No' && column !== 'Line Total' && (
                            <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSwapButtonClick(column);
                            }}
                            title={isSwapSource ? 'Cancel swap' : (isSwapMode ? 'Click to swap with this column' : 'Swap column data')}
                            style={{
                              background: isSwapSource 
                                ? '#dc2626' 
                                : (isSwapMode ? '#6b7280' : '#3b82f6'),
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '0.35rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              minHeight: '24px'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSwapSource) {
                                e.currentTarget.style.background = isSwapMode ? '#4b5563' : '#2563eb';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              } else {
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSwapSource) {
                                e.currentTarget.style.background = isSwapMode ? '#6b7280' : '#3b82f6';
                              }
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {isSwapSource ? (
                              // Cancel icon (X)
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            ) : (
                              // Swap icon (exchange arrows)
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <polyline points="1 20 1 14 7 14"></polyline>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                              </svg>
                            )}
                          </button>
                          )}
                        </div>
                        {/* Note for Cost Price */}
                        {column === 'Cost Price' && (
                          <span style={{
                            fontSize: '0.65rem',
                            color: '#6b7280',
                            fontWeight: '400',
                            fontStyle: 'italic',
                            textTransform: 'none',
                            letterSpacing: 'normal',
                            textAlign: 'center',
                            width: '100%',
                            display: 'block',
                            marginTop: '0.125rem'
                          }}>
                            (per single item)
                          </span>
                        )}
                        {Object.keys(currentFieldMapping).length > 0 && (() => {
                          // Look up the mapped field name for this column
                          const mappedField = currentFieldMapping[column];
                          if (mappedField) {
                            const mappedName = Array.isArray(mappedField) ? mappedField.join(' + ') : mappedField;
                            return (
                              <span style={{
                                fontSize: '0.7rem',
                                color: '#6b7280',
                                fontWeight: '400',
                                fontStyle: 'italic',
                                textTransform: 'none',
                                letterSpacing: 'normal',
                                textAlign: 'center',
                                width: '100%',
                                display: 'block'
                              }}>
                                ({mappedName})
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </th>
                  );
                })}
                <th
                  style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                    whiteSpace: 'nowrap',
                    minWidth: '100px'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                          <td
                    colSpan={columns.length + 1}
                    style={{
                      padding: '3rem 1rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                        No rows added yet
                      </p>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                        Click "Add Row" to start adding purchase items
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  const isRemoving = removingRows.has(rowIndex);
                  return (
                  <tr
                    key={rowIndex}
                    className={isRemoving ? 'row-removing' : ''}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: isRemoving ? '#fee2e2' : (rowIndex % 2 === 1 ? '#f9fafb' : 'white'),
                      transition: 'background-color 0.15s ease',
                      position: 'relative',
                      borderLeft: isRemoving ? '3px solid #ef4444' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isRemoving && e.currentTarget) {
                        e.currentTarget.style.backgroundColor = '#ecfdf5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isRemoving && e.currentTarget) {
                        e.currentTarget.style.backgroundColor = rowIndex % 2 === 1 ? '#f9fafb' : 'white';
                      }
                    }}
                  >
                    {columns.map((column, colIndex) => {
                      const value = row[column];
                      const isRightAligned = rightAlignColumns.includes(column);
                      const isReadOnly = column === 'S. No' || column === 'Line Total' || (column === 'Per Quantity' && row['Quantity Type'] !== 'Strip');

                      const inputStyle: React.CSSProperties = {
                        width: '100%',
                        padding: colIndex === 0 ? '0.75rem 1rem 0.75rem 0' : '0.75rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        background: isReadOnly ? '#f3f4f6' : 'white',
                        color: '#111827',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        minWidth: '150px',
                        boxSizing: 'border-box',
                        margin: 0,
                        minHeight: '48px',
                        height: 'auto'
                      };

                      return (
                        <td
                          key={column}
                          style={{
                            padding: colIndex === 0 ? '0.75rem 0.75rem 0.75rem 0' : '0.75rem',
                            textAlign: isRightAligned ? 'right' : 'left',
                            borderRight: '1px solid #f3f4f6',
                            verticalAlign: 'top',
                            margin: 0,
                            position: column === 'Medicine Name' ? 'relative' : 'static',
                            overflow: column === 'Medicine Name' ? 'visible' : 'hidden'
                          }}
                        >
                          {column === 'S. No' ? (
                            <span style={{
                              fontWeight: '600',
                              color: '#4b5563',
                              background: '#f3f4f6',
                              padding: '0.25rem 0.5rem 0.25rem 0',
                              borderRadius: '8px',
                              display: 'inline-block',
                              minWidth: '40px',
                              textAlign: 'center',
                              margin: 0
                            }}>
                              {value}
                            </span>
                          ) : column === 'Line Total' ? (
                            <span style={{
                              fontWeight: '700',
                              color: '#10b981',
                              fontSize: '0.9375rem'
                            }}>
                              {formatCurrency(Number(value))}
                            </span>
                          ) : column === 'Quantity Type' ? (
                            <select
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              style={{
                                ...inputStyle,
                                cursor: 'pointer',
                                background: String(value) !== 'Strip' && String(value) !== 'None' ? '#fff7ed' : 'white',
                                border: String(value) !== 'Strip' && String(value) !== 'None' ? '1px solid #f97316' : undefined,
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                            >
                              {QUANTITY_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : column === 'Expiry Date' ? (
                            (() => {
                              const fieldKey = `${rowIndex}-${column}`;
                              const rawValue = String(value || '');
                              // Use display value if available (user is actively typing - shows MM-YYYY)
                              // Otherwise, format the raw value for display (shows MM-YYYY only, no DD)
                              let displayValue = numericDisplayValues[fieldKey];
                              if (displayValue === undefined) {
                                // Not actively typing - show MM-YYYY format (no DD)
                                const formatted = convertToDateFormat(rawValue);
                                // If formatted exists and is MM-YYYY, use it directly (no "01-" prefix)
                                if (formatted && /^\d{2}-\d{4}$/.test(formatted)) {
                                  displayValue = formatted;
                                } else if (!formatted && rawValue && (/^\d{1,2}-\d{1,4}$/.test(rawValue) || /^\d{1,2}\/\d{1,4}$/.test(rawValue))) {
                                  // User input format, use MM-YYYY directly
                                  const mmYyyy = rawValue.replace('/', '-');
                                  if (/^\d{2}-\d{4}$/.test(mmYyyy)) {
                                    displayValue = mmYyyy;
                                  } else {
                                    displayValue = rawValue;
                                  }
                                } else {
                                  displayValue = formatted || rawValue;
                                }
                              } else {
                                // User is actively typing - ensure it's MM-YYYY format (remove "01-" prefix if present)
                                if (displayValue && /^01-(\d{2}-\d{4})$/.test(displayValue.trim())) {
                                  displayValue = displayValue.trim().substring(3);
                                }
                              }
                              
                              const handleFocus = () => {
                                // When user focuses, ensure display value is MM-YYYY format
                                const currentDisplay = numericDisplayValues[fieldKey];
                                if (!currentDisplay) {
                                  // If no display value, extract MM-YYYY from rawValue
                                  const formatted = convertToDateFormat(rawValue);
                                  if (formatted && /^\d{2}-\d{4}$/.test(formatted)) {
                                    setNumericDisplayValues(prev => ({
                                      ...prev,
                                      [fieldKey]: formatted
                                    }));
                                  }
                                } else if (currentDisplay && /^01-(\d{2}-\d{4})$/.test(currentDisplay.trim())) {
                                  // Remove "01-" prefix if somehow present
                                  const mmYyyy = currentDisplay.trim().substring(3);
                                  setNumericDisplayValues(prev => ({
                                    ...prev,
                                    [fieldKey]: mmYyyy
                                  }));
                                }
                              };
                              
                              const handleBlur = () => {
                                let currentValue = numericDisplayValues[fieldKey] || rawValue;
                                // If display shows "01-MM-YYYY", extract "MM-YYYY" for processing
                                if (currentValue && /^01-(\d{2}-\d{4})$/.test(currentValue.trim())) {
                                  currentValue = currentValue.trim().substring(3); // Remove "01-" prefix
                                }
                                if (currentValue.trim() !== '') {
                                  const normalized = parseAndNormalizeDate(currentValue);
                                  const updatedRows = [...rows];
                                  updatedRows[rowIndex] = { ...updatedRows[rowIndex], 'Expiry Date': normalized };
                                  setRows(updatedRows);
                                  // Clear display value so it shows MM-YYYY format on next render
                                  setNumericDisplayValues(prev => {
                                    const newValues = { ...prev };
                                    delete newValues[fieldKey];
                                    return newValues;
                                  });
                                }
                              };
                              
                              return isExpiryDateWithin31Days(rawValue) ? (
                                <div style={{
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  padding: '2px',
                                  borderRadius: '8px',
                                  minWidth: '150px',
                                  width: '100%'
                                }}>
                                  <input
                                    ref={(el) => { fieldRefs.current[fieldKey] = el; }}
                                    type="text"
                                    value={displayValue}
                                    onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    placeholder="MM-YYYY"
                                    pattern="\d{2}-\d{4}"
                                    style={{
                                      ...inputStyle,
                                      minWidth: '150px',
                                      background: 'white',
                                      height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px',
                                      border: 'none',
                                      borderRadius: '6px'
                                    }}
                                  />
                                </div>
                              ) : (
                                <input
                                  ref={(el) => { fieldRefs.current[fieldKey] = el; }}
                                  type="text"
                                  value={displayValue}
                                  onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="MM-YYYY"
                                  pattern="\d{2}-\d{4}"
                                  style={{
                                    ...inputStyle,
                                    minWidth: '150px',
                                    background: 'white',
                                    height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                  }}
                                />
                              );
                            })()
                          ) : column === 'Medicine Name' ? (
                            <div style={{ 
                              position: 'relative', 
                              width: '100%',
                              zIndex: showDropdown[rowIndex] ? 1000 : 1
                            }} ref={(el) => { dropdownRefs.current[rowIndex] = el; }}>
                              <textarea
                                ref={(el) => { 
                                  inputRefs.current[rowIndex] = el;
                                  fieldRefs.current[`${rowIndex}-Medicine Name`] = el;
                                }}
                                value={value}
                                onChange={(e) => {
                                  handleMedicineNameChange(rowIndex, e.target.value);
                                  autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                                }}
                                placeholder="Enter Medicine Name"
                                rows={1}
                                style={{
                                  ...inputStyle,
                                  cursor: 'text',
                                  position: 'relative',
                                  zIndex: showDropdown[rowIndex] ? 10 : 1,
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  minHeight: '48px',
                                  maxHeight: '200px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.5',
                                  height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                }}
                                onKeyDown={(e) => handleArrowKeyNavigation(e, rowIndex, 'Medicine Name')}
                                onFocus={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.borderColor = '#10b981';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                                  }
                                  if (medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0) {
                                    setShowDropdown(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                  }
                                }}
                                onBlur={(e) => {
                                  // Delay to allow dropdown click
                                  const target = e.currentTarget;
                                  setTimeout(() => {
                                    if (target) {
                                      target.style.borderColor = '#d1d5db';
                                      target.style.boxShadow = 'none';
                                    }
                                  }, 200);
                                }}
                                onInput={(e) => {
                                  autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                                }}
                              />
                            </div>
                          ) : (column === 'Composition Name' || column === 'Brand Name' || 
                                column === 'Batch Number' || column === 'HSN Code' || column === 'storage_location') ? (
                            <textarea
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, column, e.target.value);
                                autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              rows={1}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '48px',
                                maxHeight: '200px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onKeyDown={(e) => !isReadOnly && handleArrowKeyNavigation(e, rowIndex, column)}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#10b981';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                }
                                autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              onInput={(e) => {
                                autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                              }}
                            />
                          ) : (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              value={(
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'MRP' || 
                                    column === 'Selling Price' ||
                                    column === 'GST Rate' ||
                                    column === 'Total Amount' ||
                                    column === 'Free')
                                ? (numericDisplayValues[`${rowIndex}-${column}`] !== undefined 
                                    ? numericDisplayValues[`${rowIndex}-${column}`] 
                                    : (value === 0 ? '' : String(value)))
                                : String(value)}
                              onChange={(e) => {
                                  const isNumericField =
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'MRP' || 
                                    column === 'Selling Price' ||
                                    column === 'GST Rate' ||
                                    column === 'Total Amount' ||
                                    column === 'Free';
                                if (isNumericField) {
                                  // Allow decimals for all numeric fields
                                  const filteredValue = handleDecimalInput(e.target.value);
                                  handleCellChange(rowIndex, column, filteredValue);
                                } else {
                                  handleCellChange(rowIndex, column, e.target.value);
                                }
                              }}
                              placeholder={column === 'Cost Price' ? 'Enter cost per single item' : `Enter ${column}${column === 'GST Rate' ? ' (%)' : ''}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                background: isReadOnly ? '#f3f4f6' : 'white',
                                opacity: isReadOnly ? 0.7 : 1,
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onKeyDown={(e) => !isReadOnly && handleArrowKeyNavigation(e, rowIndex, column)}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#10b981';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                  // Clear display value on blur to show clean number
                                  const isNumericField =
                                    column === 'Quantity' ||
                                    column === 'Per Quantity' ||
                                    column === 'Amount' ||
                                    column === 'Discount' ||
                                    column === 'Cost Price' ||
                                    column === 'Total Amount' ||
                                    column === 'Free' ||
                                    column === 'MRP' ||
                                    column === 'Selling Price' ||
                                    column === 'GST Rate';
                                  if (isNumericField) {
                                    setNumericDisplayValues(prev => {
                                      const newValues = { ...prev };
                                      delete newValues[`${rowIndex}-${column}`];
                                      return newValues;
                                    });
                                  }
                                }
                              }}
                            />
                          )}
                        </td>
                      );
                    })}
                    {/* Actions Column - Delete Button */}
                    <td
                      style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        borderRight: '1px solid #f3f4f6',
                        verticalAlign: 'middle'
                      }}
                    >
                      <button
                        onClick={() => handleRemoveRow(rowIndex)}
                        style={{
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                          minWidth: '90px',
                          margin: '0 auto'
                        }}
                        onMouseEnter={(e) => {
                          if (e.currentTarget) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (e.currentTarget) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Remove
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        
        {/* Full-width Dropdown - Positioned relative to table container */}
        {activeDropdownRow !== null && showDropdown[activeDropdownRow] && medicineSuggestions[activeDropdownRow] && medicineSuggestions[activeDropdownRow].length > 0 && (
          <div
            ref={(el) => { if (activeDropdownRow !== null) dropdownRefs.current[activeDropdownRow] = el; }}
            style={{
              position: 'absolute',
              top: inputRefs.current[activeDropdownRow] && tableContainerRef.current
                ? `${inputRefs.current[activeDropdownRow]!.getBoundingClientRect().bottom - tableContainerRef.current.getBoundingClientRect().top + 2}px`
                : 'auto',
              left: 0,
              right: 0,
              width: '100%',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '0',
              boxShadow: 'none',
              zIndex: 1001,
              height: '270px', // Fixed height to show exactly 3 items (90px per item)
              overflowY: 'auto'
            }}
          >
            {medicineSuggestions[activeDropdownRow].map((medicine, medIndex) => (
              <div
                key={medicine.key}
                data-dropdown-item
                onClick={() => handleMedicineSelect(activeDropdownRow, medicine)}
                style={{
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  borderBottom: medIndex < medicineSuggestions[activeDropdownRow].length - 1 ? '1px solid #f3f4f6' : 'none',
                  transition: 'background-color 0.15s ease',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  flexWrap: 'wrap'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#f0fdf4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                {/* Medicine Image */}
                {medicine.Image_URL && (
                  <div 
                    style={{
                    flexShrink: 0,
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      const maximizeBtn = e.currentTarget.querySelector('[data-maximize-btn]') as HTMLElement;
                      if (maximizeBtn) {
                        maximizeBtn.style.opacity = '1';
                        maximizeBtn.style.pointerEvents = 'auto';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const maximizeBtn = e.currentTarget.querySelector('[data-maximize-btn]') as HTMLElement;
                      if (maximizeBtn) {
                        maximizeBtn.style.opacity = '0';
                        maximizeBtn.style.pointerEvents = 'none';
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onImageClick) {
                        onImageClick(medicine.Image_URL, medicine.Name);
                      }
                    }}
                  >
                    <img 
                      src={medicine.Image_URL} 
                      alt={medicine.Name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <button
                      data-maximize-btn
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onImageClick) {
                          onImageClick(medicine.Image_URL, medicine.Name);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '24px',
                        height: '24px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: 'none',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'all 0.2s ease',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                      </svg>
                    </button>
                  </div>
                )}
                
                {/* Medicine Name */}
                <div style={{
                  flex: '1 1 200px',
                  minWidth: '150px'
                }}>
                  <div style={{
                    fontWeight: '600',
                    color: '#1f2937',
                    fontSize: '0.875rem',
                    marginBottom: '0.25rem'
                  }}>
                    {medicine.Name}
                  </div>
                </div>
                
                {/* Manufacturer */}
                <div style={{
                  flex: '1 1 150px',
                  minWidth: '120px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Manufacturer:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151'
                  }}>
                    {medicine.Manufacturer}
                  </div>
                </div>
                
                {/* Salt Composition */}
                <div style={{
                  flex: '1 1 200px',
                  minWidth: '150px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Composition:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151'
                  }}>
                    {medicine.Salt_Composition}
                  </div>
                </div>
                
                {/* Quantity */}
                <div style={{
                  flex: '0 0 120px',
                  textAlign: 'right'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Quantity:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151',
                    fontWeight: '600'
                  }}>
                    {medicine.Quantity}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      )}

      {/* Summary and Submit Button - Integrated from Purchases-buttons.tsx */}
      {rows.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          paddingTop: '0.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
          width: '100%',
          paddingLeft: isMobile ? '0.5rem' : '0',
          paddingRight: isMobile ? '0.5rem' : '0'
        }}>
          {/* Summary Section - Left Side (Totals + Invoice fields) */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '0.75rem' : '1.5rem',
              width: isMobile ? '100%' : 'auto',
              alignItems: isMobile ? 'stretch' : 'flex-start',
            }}
          >
            {/* Totals */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                width: isMobile ? '100%' : 'auto',
                alignItems: isMobile ? 'center' : 'flex-start',
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  color: '#374151',
                }}
              >
                Total Items:{' '}
                <span style={{ color: '#10b981', fontWeight: '700' }}>{rows.length}</span>
              </div>
              <div
                style={{
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  color: '#374151',
                }}
              >
                Total Value:{' '}
                <span style={{ color: '#10b981', fontWeight: '700' }}>
                  {formatCurrency(calculateTotalValue())}
                </span>
              </div>
            </div>

            {/* Invoice details */}
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '0.5rem' : '1rem',
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  minWidth: isMobile ? '100%' : '220px',
                }}
              >
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#4b5563',
                  }}
                >
                  Invoice Number (optional)
                </label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Enter invoice number"
                  style={{
                    padding: '0.35rem 0.6rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.8rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  minWidth: isMobile ? '100%' : '200px',
                }}
              >
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#4b5563',
                  }}
                >
                  Invoice Date (optional)
                </label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.8rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '0.75rem',
            width: isMobile ? '100%' : 'auto'
          }}>
            {hasAnyRowData && (
              <button
                type="button"
                onClick={handleClearAll}
                disabled={isSubmitting}
                style={{
                  background: 'transparent',
                  color: '#ef4444',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '8px',
                  padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  justifyContent: 'center',
                  width: isMobile ? '100%' : 'auto',
                  opacity: isSubmitting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && e.currentTarget) {
                    e.currentTarget.style.background = '#fef2f2';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                <span>Clear All</span>
              </button>
            )}
            <button
              onClick={handleSubmit}
              type="button"
              disabled={isSubmitting}
              style={{
                background: isSubmitting 
                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: isMobile ? '0.75rem 1.5rem' : '0.875rem 2rem',
                fontSize: isMobile ? '0.875rem' : '0.9375rem',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: isSubmitting 
                  ? '0 2px 4px rgba(156, 163, 175, 0.2)'
                  : '0 4px 12px rgba(16, 185, 129, 0.3)',
                outline: 'none',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center',
                opacity: isSubmitting ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && e.currentTarget) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && e.currentTarget) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }
              }}
              onFocus={(e) => {
                if (!isSubmitting && e.currentTarget) {
                  e.currentTarget.style.outline = '2px solid rgba(16, 185, 129, 0.5)';
                  e.currentTarget.style.outlineOffset = '2px';
                }
              }}
              onBlur={(e) => {
                if (e.currentTarget) {
                  e.currentTarget.style.outline = 'none';
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Submit</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Validation Notification */}
      {showValidationPopup && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 10000,
            maxWidth: '380px',
            width: '100%',
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1rem',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              borderLeft: '4px solid #ef4444'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '0.75rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                flex: 1
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '0.9375rem',
                    color: '#1f2937',
                    marginBottom: '0.5rem'
                  }}>
                    Missing Required Fields
                  </div>
                  <div style={{
                    fontSize: '0.8125rem',
                    color: '#6b7280',
                    marginBottom: '0.75rem',
                    lineHeight: '1.4'
                  }}>
                    {validationErrors.length} {validationErrors.length === 1 ? 'row has' : 'rows have'} incomplete fields
                  </div>
                  {validationErrors.length > 0 && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#991b1b',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      paddingRight: '0.25rem'
                    }}>
                      {validationErrors.map((error, index) => (
                        <div key={index} style={{
                          background: '#fef2f2',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          borderLeft: '2px solid #ef4444'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                            Row #{error.rowNumber}:
                          </div>
                          <div style={{ color: '#7f1d1d' }}>
                            {error.missingFields.join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowValidationPopup(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  color: '#6b7280',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes slideInRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes rowRemove {
          0% {
            opacity: 1;
            transform: translateX(0);
            max-height: 1000px;
          }
          100% {
            opacity: 0;
            transform: translateX(-100%);
            max-height: 0;
            margin: 0;
            padding: 0;
          }
        }
        .row-removing {
          animation: rowRemove 0.3s ease-out forwards !important;
          overflow: hidden;
        }
        .row-removing * {
          pointer-events: none;
        }
        /* Thin modern horizontal scrollbar (overrides index.css global hide) */
        .purchase-table-scroll-container {
          -ms-overflow-style: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(113, 113, 122, 0.55) rgba(24, 24, 27, 0.06);
        }
        .purchase-table-scroll-container::-webkit-scrollbar {
          display: block;
          height: 6px;
          -webkit-appearance: none;
        }
        .purchase-table-scroll-container::-webkit-scrollbar-track {
          background: rgba(24, 24, 27, 0.06);
          border-radius: 100px;
          margin: 0 6px;
        }
        .purchase-table-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(113, 113, 122, 0.45);
          border-radius: 100px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: background-color 0.15s ease;
          min-width: 40px;
        }
        .purchase-table-scroll-container::-webkit-scrollbar-thumb:hover {
          background-color: rgba(82, 82, 91, 0.6);
          background-clip: padding-box;
        }
        .purchase-table-scroll-container::-webkit-scrollbar-thumb:active {
          background-color: rgba(63, 63, 70, 0.75);
          background-clip: padding-box;
        }
        .purchase-table-scroll-container::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </div>
    </>
  );
};

export default EditablePurchaseTable;
